import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ET_TIMEZONE,
  formatMarketSentiment,
  getSentimentEmoji,
  getSentimentColor,
  getSpreadEmoji,
  getSpreadColor,
} from './utils/marketUtils';
import { formatModelName } from './utils/modelName';
import { AI_PROVIDER } from './constants/ai';
import { parseAction } from './utils/actionParser';
import {
  RISK_SCORE_COLOR,
  DEFAULT_POSITION_DOLLARS,
  DEFAULT_ACCOUNT_SIZE,
  POSITION_DOLLARS_OPTIONS,
  TOTAL_RISK_THRESHOLDS,
  RVOL_HIGH_THRESHOLD,
  RVOL_TIERS,
  GAP_THRESHOLDS,
  STOP_VWAP_THRESHOLDS,
  RR_THRESHOLDS,
  LOSS_PCT_THRESHOLDS,
} from './constants/riskMetrics';
import {
  computeMetrics,
  computeShares,
  computeMaxLoss,
  computeTotalRisk,
  computeVwapDistance,
  abbreviateSector,
} from './utils/riskMetrics';

// Render star rating
const renderStars = (rating) => {
  return '⭐'.repeat(Math.min(rating, 3));
};

// Action Display Component
const ActionDisplay = ({ action }) => {
  const parsed = parseAction(action);

  // If parsing failed, show raw action
  if (!parsed) {
    return (
      <div style={{ color: '#2563eb', marginTop: '4px' }}>
        <strong>Action:</strong> {action}
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '6px',
      padding: '8px 10px',
      backgroundColor: 'rgba(37, 99, 235, 0.05)',
      borderRadius: '4px',
      borderLeft: '3px solid #2563eb'
    }}>
      <div style={{
        fontWeight: '600',
        color: '#1e40af',
        marginBottom: '4px',
        fontSize: '0.9em'
      }}>
        Action Plan
      </div>
      {parsed.entry && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>🟢</span>
          <span><strong style={{ color: '#059669' }}>Entry:</strong> {parsed.entry}</span>
        </div>
      )}
      {parsed.stopLoss && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>🛑</span>
          <span><strong style={{ color: '#dc2626' }}>Stop:</strong> {parsed.stopLoss}</span>
        </div>
      )}
      {parsed.takeProfitPrice && parsed.entryPrice && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>🎯</span>
          <span>
            <strong style={{ color: '#7c3aed' }}>Target:</strong> ${parsed.takeProfitPrice.toFixed(2)}
            <span style={{ color: '#6b7280', fontSize: '0.9em' }}>
              {' '}(+{((parsed.takeProfitPrice - parsed.entryPrice) / parsed.entryPrice * 100).toFixed(1)}%)
            </span>
          </span>
        </div>
      )}
      {parsed.targets && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
          <span style={{ flexShrink: 0 }}>📍</span>
          <div>
            <strong style={{ color: '#7c3aed' }}>T1/T2/T3:</strong>
            <div style={{
              marginTop: '2px',
              marginLeft: '4px',
              fontSize: '0.95em',
              lineHeight: '1.6'
            }}>
              <div style={{ color: '#059669' }}>
                T1: ${parsed.targets.t1.price.toFixed(2)} <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t1.percent}%)</span>
              </div>
              <div style={{ color: '#2563eb' }}>
                T2: ${parsed.targets.t2.price.toFixed(2)} <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t2.percent}%)</span>
              </div>
              <div style={{ color: '#7c3aed' }}>
                T3: ${parsed.targets.t3.price.toFixed(2)} <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t3.percent}%)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// AI result error → human-readable display. The raw error string
// (e.g. "API error 504") goes verbatim into the tooltip for debugging;
// the visible text says what happened + what to do. Without this, a
// failed AI render was indistinguishable from a loading one — the
// column showed "⏳ Waiting..." forever and the only way to learn of
// the failure was the browser console.
const aiErrorDisplay = (error) => {
  const e = (error || '').toLowerCase();
  if (e.includes('504') || e.includes('timeout'))
    return { text: 'timed out', hint: 'Hit Refresh to retry' };
  if (e.includes('network') || e.includes('failed to fetch') || e.includes('offline'))
    return { text: 'unreachable', hint: 'Backend offline?' };
  return { text: 'failed', hint: 'Try Refresh' };
};

// Risk badge color helpers
const getRiskBadgeStyle = (riskLevel) => {
  switch(riskLevel) {
    case 'LOW': return { bg: '#dcfce7', color: '#166534', border: '#22c55e' };
    case 'MEDIUM': return { bg: '#fef3c7', color: '#92400e', border: '#eab308' };
    case 'HIGH': return { bg: '#fee2e2', color: '#991b1b', border: '#ef4444' };
    default: return { bg: '#f3f4f6', color: '#374151', border: '#6b7280' };
  }
};

const getRecBadgeStyle = (rec) => {
  switch(rec) {
    case 'TAKE': return { bg: '#dcfce7', color: '#166534' };
    case 'REDUCE SIZE': return { bg: '#fef3c7', color: '#92400e' };
    case 'SKIP': return { bg: '#fee2e2', color: '#991b1b' };
    default: return { bg: '#f3f4f6', color: '#374151' };
  }
};

// Detailed Pick Card Component (Phase 2: Learning Loop - Personalization)
// Background follows the action recommendation (TAKE/REDUCE/SKIP) when stockRisk
// is available, else falls back to column color (Claude purple / GPT green).
// Same recommendation = same bg across all sections.
const DetailedPickCard = ({ pick, index, bgColor, borderColor, stockRisk, stock, onSymbolClick }) => {
  // Cap + Sector subline — same emoji language as Modal + Context
  // Strip Row 2. Lets the user judge liquidity tier + sector rotation
  // context without opening the modal.
  const capTier = stock?.marketCap ? getCapTier(stock.marketCap) : null;
  const capLabel = capTier ? CAP_TIER_LABELS[capTier] : null;
  const sectorLabel = getSectorChip(stock?.sicCode, stock?.sicDescription);
  const riskBg = stockRisk && stockRisk.recommendation ? (() => {
    const rec = stockRisk.recommendation.toUpperCase();
    if (rec.includes('SKIP')) return '#fef2f2';
    if (rec.includes('REDUCE')) return '#fefce8';
    if (rec.includes('TAKE')) return '#f0fdf4';
    return null;
  })() : null;
  return (
  <div style={{
    backgroundColor: riskBg || bgColor,
    padding: '10px 12px',
    borderRadius: '6px',
    marginBottom: '8px',
    borderLeft: `3px solid ${borderColor}`
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '6px',
      flexWrap: 'wrap',
      gap: '6px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span
          onClick={() => onSymbolClick?.(pick.symbol)}
          title={onSymbolClick ? 'View risk assessment' : undefined}
          style={{
            fontWeight: 'bold',
            fontSize: '0.95em',
            cursor: onSymbolClick ? 'pointer' : 'default',
            textDecoration: onSymbolClick ? 'underline dotted' : 'none',
            textDecorationColor: '#9ca3af',
            textUnderlineOffset: '3px',
          }}
        >
          {index + 1}. {pick.symbol} {renderStars(pick.rating)}
        </span>

        {/* Risk Score — Pattern C tier emoji + N/10 so the AI-comparison
            view shows risk at a glance without opening the deep-dive modal */}
        {stockRisk?.riskScore != null && (() => {
          const d = riskScoreDisplay(stockRisk.riskScore);
          return (
            <span style={{ color: d.color, fontWeight: '600', fontSize: '0.85em' }}>
              {d.icon} {d.text}
            </span>
          );
        })()}

        {/* Phase 2: Personalization badges - Caution takes priority */}
        {pick.matchesUserSuccessPattern && !pick.matchesUserAvoidPattern && (
          <span style={{
            display: 'inline-block',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '0.75em',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            color: '#92400e',
            border: '1px solid #f59e0b'
          }} title={`Matches your proven pattern: ${pick.userSuccessPatternName || 'successful pattern'}`}>
            ✨ Proven
          </span>
        )}

        {pick.matchesUserAvoidPattern && (
          <span style={{
            display: 'inline-block',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '0.75em',
            fontWeight: '600',
            background: '#fee2e2',
            color: '#991b1b',
            border: '1px solid #dc2626'
          }} title={`Similar to losing pattern: ${pick.userAvoidPatternName || 'avoid pattern'}`}>
            ⚠️ Caution
          </span>
        )}

        {/* Recommendation badge inline — clickable, opens deep-dive modal
            consistent with the Top Picks chip behavior. */}
        {stockRisk && (() => {
          const rc = getRecBadgeStyle(stockRisk.recommendation);
          return (
            <span
              onClick={(e) => { e.stopPropagation(); onSymbolClick?.(pick.symbol); }}
              title={onSymbolClick ? 'View risk assessment' : undefined}
              style={{
                display: 'inline-block',
                padding: '2px 5px',
                borderRadius: '3px',
                fontSize: '0.7em',
                fontWeight: '600',
                backgroundColor: rc.bg,
                color: rc.color,
                cursor: onSymbolClick ? 'pointer' : 'default',
              }}
            >
              {stockRisk.recommendation}
            </span>
          );
        })()}
      </div>

      <span style={{
        color: pick.dailyGain >= 0 ? '#22c55e' : '#ef4444',
        fontWeight: '600',
        fontSize: '0.95em'
      }}>
        {pick.dailyGain >= 0 ? '+' : ''}{pick.dailyGain?.toFixed(2)}%
      </span>
    </div>

    {/* Cap + Sector subline — gray secondary info between header
        badges and Trend. Skipped silently when stock data hasn't
        loaded (no placeholder noise during initial scan). */}
    {(capLabel || sectorLabel) && (
      <div style={{
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        color: '#6b7280',
        fontSize: '0.78em',
        marginBottom: '4px',
      }}>
        {capLabel && (
          <span title={`${capLabel.name} cap: ${capLabel.desc}`}>
            {capLabel.emoji} {capLabel.name}
          </span>
        )}
        {capLabel && sectorLabel && <span style={{ color: '#d1d5db' }}>·</span>}
        {sectorLabel && (
          <span>{sectorLabel.emoji} {sectorLabel.name}</span>
        )}
      </div>
    )}

    <div style={{ fontSize: '0.85em', color: '#4b5563', lineHeight: '1.5' }}>
      <div><strong>Trend:</strong> {pick.trend}</div>
      {pick.pattern && <div><strong>Pattern:</strong> {pick.pattern}</div>}
      <ActionDisplay action={pick.action} />
    </div>

    {/* Phase 2: Personalization note — single-rule cross-surface
        styling (header badge + colored block). No inner badge: the
        ✨ Proven / ⚠️ Caution signal already lives in the header
        badge (line 183-221) and in this block's background color. */}
    {pick.personalizationNote && (() => {
      const isCaution = pick.matchesUserAvoidPattern;
      return (
        <div style={{
          background: isCaution ? '#fef2f2' : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderLeft: isCaution ? '3px solid #dc2626' : '3px solid #f59e0b',
          padding: '8px 10px',
          margin: '8px 0 0 0',
          borderRadius: '4px',
          fontSize: '0.8em',
          color: isCaution ? '#991b1b' : '#78350f',
          fontWeight: '500',
          lineHeight: '1.4'
        }}>
          {pick.personalizationNote.replace(/^[✨⚠️]\s*/, '')}
        </div>
      );
    })()}

    {/* Risk Assessment block removed — full risk reasoning is in Risk Assessment section below.
        Inline recommendation badge at top of card already shows TAKE/REDUCE SIZE/SKIP. */}
  </div>
  );
};

// Detailed Top Picks Comparison Component
// Renders progressively: each AI column shows when ready, the other shows a placeholder.
const DetailedPicksComparison = ({ anthropicResult, openaiResult, riskAssessment, stockMap, onSymbolClick }) => {
  const claudeReady = anthropicResult && !anthropicResult.error;
  const gptReady = openaiResult && !openaiResult.error;
  // Render nothing until at least one AI returns successfully
  if (!claudeReady && !gptReady) return null;

  const claudePicksData = claudeReady ? (anthropicResult.topPicks || []) : [];
  const gptPicksData = gptReady ? (openaiResult.topPicks || []) : [];
  const maxPicks = Math.max(claudePicksData.length, gptPicksData.length);

  // If both ready but no picks, nothing to show
  if (claudeReady && gptReady && maxPicks === 0) return null;

  // Build a lookup map from risk assessment: symbol → StockRisk
  const riskMap = {};
  if (riskAssessment?.stockRisks) {
    for (const risk of riskAssessment.stockRisks) {
      riskMap[risk.symbol] = risk;
    }
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fef3c7',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '1.2em' }}>🔥</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>Detailed Top Picks Comparison</span>
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px'
        }}>
          {/* Claude Column */}
          <div>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '10px',
              color: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.95em',
              paddingBottom: '8px',
              borderBottom: '2px solid #7c3aed',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1
            }}>
              {/* Real model from the response — under the dual-model trial
                  the manual path may run a different model than auto scans,
                  so a build-time name can't be trusted. Plain 'Claude'
                  while loading (model genuinely unknown until it answers). */}
              <span>🟣</span> {formatModelName(anthropicResult?.model) || 'Claude'}
              {!claudeReady && !anthropicResult?.error && (
                <span style={{ fontSize: '0.85em', color: '#9ca3af', fontWeight: 'normal', marginLeft: '8px' }}>
                  ⏳ analyzing...
                </span>
              )}
              {anthropicResult?.error && (
                <span
                  title={anthropicResult.error}
                  style={{ fontSize: '0.85em', color: '#dc2626', fontWeight: '600', marginLeft: '8px' }}
                >
                  ⚠️ {aiErrorDisplay(anthropicResult.error).text}
                </span>
              )}
            </div>
            <div style={{
              maxHeight: '600px',
              overflowY: 'auto',
              paddingRight: '4px'
            }}>
              {!claudeReady && !anthropicResult?.error && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', fontSize: '0.9em' }}>
                  Waiting for Claude to analyze...
                </div>
              )}
              {anthropicResult?.error && (
                <div
                  title={anthropicResult.error}
                  style={{ padding: '20px', textAlign: 'center', color: '#dc2626', fontSize: '0.9em' }}
                >
                  ⚠️ Claude analysis {aiErrorDisplay(anthropicResult.error).text} — {aiErrorDisplay(anthropicResult.error).hint}
                </div>
              )}
              {claudePicksData.map((pick, i) => (
                <DetailedPickCard
                  key={pick.symbol}
                  pick={pick}
                  index={i}
                  bgColor="#f9fafb"
                  borderColor={pick.rating >= 3 ? '#22c55e' : pick.rating >= 2 ? '#eab308' : '#6b7280'}
                  stockRisk={riskMap[pick.symbol]}
                  stock={stockMap?.[pick.symbol]}
                  onSymbolClick={onSymbolClick}
                />
              ))}
            </div>
          </div>

          {/* GPT Column */}
          <div>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '10px',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.95em',
              paddingBottom: '8px',
              borderBottom: '2px solid #059669',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1
            }}>
              <span>🟢</span> {formatModelName(openaiResult?.model) || 'GPT'}
              {!gptReady && !openaiResult?.error && (
                <span style={{ fontSize: '0.85em', color: '#9ca3af', fontWeight: 'normal', marginLeft: '8px' }}>
                  ⏳ analyzing...
                </span>
              )}
              {openaiResult?.error && (
                <span
                  title={openaiResult.error}
                  style={{ fontSize: '0.85em', color: '#dc2626', fontWeight: '600', marginLeft: '8px' }}
                >
                  ⚠️ {aiErrorDisplay(openaiResult.error).text}
                </span>
              )}
            </div>
            <div style={{
              maxHeight: '600px',
              overflowY: 'auto',
              paddingRight: '4px'
            }}>
              {!gptReady && !openaiResult?.error && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic', fontSize: '0.9em' }}>
                  Waiting for GPT to analyze...
                </div>
              )}
              {openaiResult?.error && (
                <div
                  title={openaiResult.error}
                  style={{ padding: '20px', textAlign: 'center', color: '#dc2626', fontSize: '0.9em' }}
                >
                  ⚠️ GPT analysis {aiErrorDisplay(openaiResult.error).text} — {aiErrorDisplay(openaiResult.error).hint}
                </div>
              )}
              {gptPicksData.map((pick, i) => (
                <DetailedPickCard
                  key={pick.symbol}
                  pick={pick}
                  index={i}
                  bgColor="#f9fafb"
                  borderColor={pick.rating >= 3 ? '#22c55e' : pick.rating >= 2 ? '#eab308' : '#6b7280'}
                  stockRisk={riskMap[pick.symbol]}
                  stock={stockMap?.[pick.symbol]}
                  onSymbolClick={onSymbolClick}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Comparison Summary Component
// Renders progressively: each AI's column shows when that AI is done, the other
// shows a placeholder. Both Recommend (consensus) only shows when both done.
const ComparisonSummary = ({ anthropicResult, openaiResult, riskAssessment, onSymbolClick }) => {
  const claudeReady = anthropicResult && !anthropicResult.error;
  const gptReady = openaiResult && !openaiResult.error;
  // Render nothing until at least one AI returns successfully
  if (!claudeReady && !gptReady) return null;

  const claudePicksData = claudeReady ? (anthropicResult.topPicks || []) : [];
  const gptPicksData = gptReady ? (openaiResult.topPicks || []) : [];
  const claudePicks = claudePicksData.map(p => p.symbol);
  const gptPicks = gptPicksData.map(p => p.symbol);

  // Find consensus (requires both AIs done)
  const bothReady = claudeReady && gptReady;
  const consensusPicks = bothReady ? claudePicks.filter(s => gptPicks.includes(s)) : [];

  // Risk Assessment lookup: symbol → stockRisk (riskLevel, recommendation, etc.)
  const riskMap = {};
  if (riskAssessment?.stockRisks) {
    for (const risk of riskAssessment.stockRisks) {
      riskMap[risk.symbol] = risk;
    }
  }
  // Background color by recommendation (TAKE/REDUCE/SKIP), consistent across
  // all three sections (Top Picks, Detailed, Risk Assessment). Same recommendation
  // = same bg color regardless of risk level — color tells the user the ACTION.
  // Returns null when stockRisk unavailable (caller falls back to default bg).
  const getRiskBg = (symbol) => {
    const risk = riskMap[symbol];
    if (!risk || !risk.recommendation) return null;
    const rec = risk.recommendation.toUpperCase();
    if (rec.includes('SKIP')) return '#fef2f2';
    if (rec.includes('REDUCE')) return '#fefce8';
    if (rec.includes('TAKE')) return '#f0fdf4';
    return null;
  };
  // Inline badge component: shows SKIP (red), REDUCE (yellow), TAKE (green)
  // for consistency with Detailed Top Picks Comparison and Risk Assessment sections
  const renderRiskBadge = (symbol) => {
    const risk = riskMap[symbol];
    if (!risk || !risk.recommendation) return null;
    const rec = risk.recommendation.toUpperCase();
    let style, label;
    if (rec.includes('SKIP')) {
      style = { backgroundColor: '#fee2e2', color: '#991b1b' };
      label = 'SKIP';
    } else if (rec.includes('REDUCE')) {
      style = { backgroundColor: '#fef3c7', color: '#92400e' };
      label = 'REDUCE';
    } else if (rec.includes('TAKE')) {
      style = { backgroundColor: '#dcfce7', color: '#166534' };
      label = 'TAKE';
    } else {
      return null;
    }
    return (
      <span style={{
        ...style,
        padding: '1px 5px',
        borderRadius: '3px',
        fontSize: '0.7em',
        fontWeight: 'bold',
        marginLeft: '4px'
      }}>{label}</span>
    );
  };

  // Get rating for a symbol from picks data
  const getClaudeRating = (symbol) => claudePicksData.find(p => p.symbol === symbol)?.rating || 0;
  const getGptRating = (symbol) => gptPicksData.find(p => p.symbol === symbol)?.rating || 0;
  const getClaudeDailyGain = (symbol) => claudePicksData.find(p => p.symbol === symbol)?.dailyGain;
  const getGptDailyGain = (symbol) => gptPicksData.find(p => p.symbol === symbol)?.dailyGain;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fef3c7',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '1.2em' }}>🏆</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>Top Picks</span>
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* Consensus Picks */}
        {consensusPicks.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '6px',
              color: '#16a34a',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>✅</span>
              <span>Both Recommend ({consensusPicks.length})</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {consensusPicks.map(symbol => {
                const avgRating = Math.round((getClaudeRating(symbol) + getGptRating(symbol)) / 2);
                const dailyGain = getClaudeDailyGain(symbol) ?? getGptDailyGain(symbol);
                return (
                  <span
                    key={symbol}
                    onClick={() => onSymbolClick?.(symbol)}
                    title={onSymbolClick ? 'View risk assessment' : undefined}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: getRiskBg(symbol) || '#f9fafb',
                      borderRadius: '4px',
                      fontSize: '0.95em',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: onSymbolClick ? 'pointer' : 'default',
                    }}
                  >
                    <span style={{ fontWeight: '500' }}>
                      {symbol} {renderStars(avgRating)}
                      {renderRiskBadge(symbol)}
                    </span>
                    {dailyGain != null && (
                      <span style={{
                        color: dailyGain >= 0 ? '#22c55e' : '#ef4444',
                        fontWeight: '600',
                        fontSize: '0.95em'
                      }}>
                        {dailyGain >= 0 ? '+' : ''}{dailyGain.toFixed(2)}%
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Top Picks Comparison */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px'
        }}>
          {/* Claude Picks */}
          <div>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '6px',
              color: claudeReady ? getAiCountColor(claudePicks.length) : '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.9em'
            }}>
              <span title={claudeReady ? 'Claude top picks. 🟢 ≥7 confident / 🟡 5-6 normal / 🔴 ≤4 cautious (Abstain triggered)' : undefined}>
                {claudeReady ? getAiCountEmoji(claudePicks.length) : anthropicResult?.error ? '⚠️' : '⏳'}
              </span> Claude Top {claudeReady ? claudePicks.length : ''}
              {!claudeReady && !anthropicResult?.error && (
                <span style={{ fontSize: '0.85em', color: '#9ca3af', fontWeight: 'normal' }}>
                  ⏳ analyzing...
                </span>
              )}
              {anthropicResult?.error && (
                <span
                  title={anthropicResult.error}
                  style={{ fontSize: '0.85em', color: '#dc2626', fontWeight: '600' }}
                >
                  {aiErrorDisplay(anthropicResult.error).text}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {!claudeReady && !anthropicResult?.error && (
                <span style={{
                  padding: '8px',
                  fontSize: '0.85em',
                  color: '#9ca3af',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  Waiting for Claude...
                </span>
              )}
              {anthropicResult?.error && (
                <span
                  title={anthropicResult.error}
                  style={{
                    padding: '8px',
                    fontSize: '0.85em',
                    color: '#dc2626',
                    textAlign: 'center',
                  }}
                >
                  ⚠️ Claude analysis {aiErrorDisplay(anthropicResult.error).text} — {aiErrorDisplay(anthropicResult.error).hint}
                </span>
              )}
              {claudePicksData.map((pick, i) => (
                <span
                  key={pick.symbol}
                  onClick={() => onSymbolClick?.(pick.symbol)}
                  title={onSymbolClick ? 'View risk assessment' : undefined}
                  style={{
                    padding: '3px 8px',
                    backgroundColor: getRiskBg(pick.symbol) || '#f9fafb',
                    borderRadius: '4px',
                    fontSize: '0.95em',
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: onSymbolClick ? 'pointer' : 'default',
                  }}
                >
                  <span style={{ fontWeight: '500' }}>
                    {i + 1}. {pick.symbol} {renderStars(pick.rating)}
                    {renderRiskBadge(pick.symbol)}
                  </span>
                  {pick.dailyGain != null && (
                    <span style={{
                      color: pick.dailyGain >= 0 ? '#22c55e' : '#ef4444',
                      fontWeight: '600',
                      fontSize: '0.95em',
                      marginLeft: '8px'
                    }}>
                      {pick.dailyGain >= 0 ? '+' : ''}{pick.dailyGain.toFixed(2)}%
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* GPT Picks */}
          <div>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '6px',
              color: gptReady ? getAiCountColor(gptPicks.length) : '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.9em'
            }}>
              <span title={gptReady ? 'GPT top picks. Same tiers as Claude.' : undefined}>
                {gptReady ? getAiCountEmoji(gptPicks.length) : openaiResult?.error ? '⚠️' : '⏳'}
              </span> GPT Top {gptReady ? gptPicks.length : ''}
              {!gptReady && !openaiResult?.error && (
                <span style={{ fontSize: '0.85em', color: '#9ca3af', fontWeight: 'normal' }}>
                  ⏳ analyzing...
                </span>
              )}
              {openaiResult?.error && (
                <span
                  title={openaiResult.error}
                  style={{ fontSize: '0.85em', color: '#dc2626', fontWeight: '600' }}
                >
                  {aiErrorDisplay(openaiResult.error).text}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {!gptReady && !openaiResult?.error && (
                <span style={{
                  padding: '8px',
                  fontSize: '0.85em',
                  color: '#9ca3af',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  Waiting for GPT...
                </span>
              )}
              {openaiResult?.error && (
                <span
                  title={openaiResult.error}
                  style={{
                    padding: '8px',
                    fontSize: '0.85em',
                    color: '#dc2626',
                    textAlign: 'center',
                  }}
                >
                  ⚠️ GPT analysis {aiErrorDisplay(openaiResult.error).text} — {aiErrorDisplay(openaiResult.error).hint}
                </span>
              )}
              {gptPicksData.map((pick, i) => (
                <span
                  key={pick.symbol}
                  onClick={() => onSymbolClick?.(pick.symbol)}
                  title={onSymbolClick ? 'View risk assessment' : undefined}
                  style={{
                    padding: '3px 8px',
                    backgroundColor: getRiskBg(pick.symbol) || '#f9fafb',
                    borderRadius: '4px',
                    fontSize: '0.95em',
                    cursor: onSymbolClick ? 'pointer' : 'default',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontWeight: '500' }}>
                    {i + 1}. {pick.symbol} {renderStars(pick.rating)}
                    {renderRiskBadge(pick.symbol)}
                  </span>
                  {pick.dailyGain != null && (
                    <span style={{
                      color: pick.dailyGain >= 0 ? '#22c55e' : '#ef4444',
                      fontWeight: '600',
                      fontSize: '0.95em',
                      marginLeft: '8px'
                    }}>
                      {pick.dailyGain >= 0 ? '+' : ''}{pick.dailyGain.toFixed(2)}%
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Single AI result card
const AiResultCard = ({ result, isLoading, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (isLoading) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ fontSize: '1.2em' }}>⏳</span>
          <span style={{ fontWeight: 'bold', color: '#6b7280' }}>Analyzing...</span>
        </div>
        <div style={{ color: '#9ca3af' }}>Please wait while AI analyzes the stock data</div>
      </div>
    );
  }

  if (!result) return null;

  if (result.error) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
        marginBottom: '12px',
        border: '1px solid #fecaca'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '1.2em' }}>❌</span>
          <span style={{ fontWeight: 'bold', color: '#dc2626' }}>
            {result.provider === AI_PROVIDER.ANTHROPIC ? 'Claude' : 'GPT'} Error
          </span>
        </div>
        <div style={{ color: '#dc2626', fontSize: '0.9em' }}>{result.error}</div>
      </div>
    );
  }

  const providerIcon = result.provider === AI_PROVIDER.ANTHROPIC ? '🟣' : '🟢';
  const providerName = formatModelName(result.model)
      || (result.provider === AI_PROVIDER.ANTHROPIC ? 'Claude' : 'GPT');
  const bgColor = result.provider === AI_PROVIDER.ANTHROPIC ? '#faf5ff' : '#f0fdf4';

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Collapsible Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '12px 16px',
          backgroundColor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1.2em' }}>{providerIcon}</span>
          <span style={{ fontWeight: 'bold' }}>{providerName}</span>
          {result.inputTokens && (
            <span style={{ fontSize: '0.75em', color: '#9ca3af' }}>
              {result.inputTokens + result.outputTokens} tokens
            </span>
          )}
        </div>
        <span style={{ color: '#6b7280', fontSize: '1.2em' }}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div style={{ padding: '16px' }}>
          {/* Summary */}
          {result.summary && (
            <div style={{
              backgroundColor: '#f0fdf4',
              padding: '10px 14px',
              borderRadius: '6px',
              marginBottom: '12px',
              fontSize: '1em',
              color: '#166534',
              lineHeight: '1.5'
            }}>
              {result.summary}
            </div>
          )}

          {/* Top Picks */}
          {result.topPicks && result.topPicks.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>🔥</span>
                <span>Top {result.topPicks.length} Picks</span>
              </div>
              {result.topPicks.map((pick, index) => (
                <div key={pick.symbol} style={{
                  backgroundColor: '#f9fafb',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  marginBottom: '6px',
                  borderLeft: `3px solid ${pick.rating >= 3 ? '#22c55e' : pick.rating >= 2 ? '#eab308' : '#6b7280'}`
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '4px',
                    flexWrap: 'wrap',
                    gap: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 'bold' }}>
                        {index + 1}. {pick.symbol} {renderStars(pick.rating)}
                      </span>

                      {/* Phase 2: Personalization badges - Caution takes priority */}
                      {pick.matchesUserSuccessPattern && !pick.matchesUserAvoidPattern && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '0.75em',
                          fontWeight: '600',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          color: '#92400e',
                          border: '1px solid #f59e0b'
                        }} title={`Matches: ${pick.userSuccessPatternName || 'proven pattern'}`}>
                          ✨ Proven
                        </span>
                      )}

                      {pick.matchesUserAvoidPattern && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '0.75em',
                          fontWeight: '600',
                          background: '#fee2e2',
                          color: '#991b1b',
                          border: '1px solid #dc2626'
                        }} title={`Similar to: ${pick.userAvoidPatternName || 'avoid pattern'}`}>
                          ⚠️ Caution
                        </span>
                      )}
                    </div>

                    <span style={{
                      color: pick.dailyGain >= 0 ? '#22c55e' : '#ef4444',
                      fontWeight: '600',
                      fontSize: '0.95em'
                    }}>
                      {pick.dailyGain >= 0 ? '+' : ''}{pick.dailyGain?.toFixed(2)}%
                    </span>
                  </div>
                  <div style={{ fontSize: '0.95em', color: '#4b5563', lineHeight: '1.6' }}>
                    <div><strong>Trend:</strong> {pick.trend}</div>
                    {pick.pattern && <div><strong>Pattern:</strong> {pick.pattern}</div>}
                    <ActionDisplay action={pick.action} />
                  </div>

                  {/* Phase 2: Personalization note - layout matches risk detail block */}
                  {pick.personalizationNote && (() => {
                    const isCaution = pick.matchesUserAvoidPattern;
                    return (
                      <div style={{
                        background: isCaution ? '#fef2f2' : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderLeft: isCaution ? '3px solid #dc2626' : '3px solid #f59e0b',
                        padding: '8px 10px',
                        margin: '8px 0 0 0',
                        borderRadius: '4px',
                        fontSize: '0.85em',
                        color: isCaution ? '#991b1b' : '#78350f',
                        fontWeight: '500',
                        lineHeight: '1.4'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                          {isCaution ? (
                            <span style={{
                              padding: '1px 5px',
                              borderRadius: '3px',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              background: '#fee2e2',
                              color: '#991b1b',
                              border: '1px solid #dc2626'
                            }}>⚠️ Caution</span>
                          ) : (
                            <span style={{
                              padding: '1px 5px',
                              borderRadius: '3px',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                              color: '#92400e',
                              border: '1px solid #f59e0b'
                            }}>✨ Proven</span>
                          )}
                        </div>
                        <div style={{ lineHeight: '1.4' }}>
                          {pick.personalizationNote.replace(/^[✨⚠️]\s*/, '')}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}

          {/* Avoid */}
          {result.avoid && result.avoid.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#dc2626'
              }}>
                <span>⚠️</span>
                <span>Avoid</span>
              </div>
              {result.avoid.map((stock) => (
                <div key={stock.symbol} style={{
                  backgroundColor: '#fef2f2',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  marginBottom: '6px',
                  fontSize: '1em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontWeight: 'bold', color: '#dc2626' }}>{stock.symbol}</span>
                  <span style={{ color: '#7f1d1d' }}>- {stock.reason}</span>
                </div>
              ))}
            </div>
          )}

          {/* Detailed Analysis */}
          {result.analysis && (
            <div style={{ marginTop: '12px' }}>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                fontSize: '1em',
                color: '#6b7280'
              }}>
                Analysis
              </div>
              <div style={{
                fontSize: '0.95em',
                color: '#4b5563',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}>
                {result.analysis}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Risk Assessment Section Component
// Map recommendation string → filter chip category key.
const RECOMMENDATION_FILTERS = {
  TAKE: 'TAKE',
  'REDUCE SIZE': 'REDUCE',
  SKIP: 'SKIP',
};

// Group stockRisks by recommendation, then sort by riskScore ascending
// within each group (best = lowest risk first).
const groupAndSortRisks = (stockRisks) => {
  const groups = { TAKE: [], REDUCE: [], SKIP: [] };
  (stockRisks || []).forEach((r) => {
    const key = RECOMMENDATION_FILTERS[r.recommendation];
    if (key) groups[key].push(r);
  });
  Object.values(groups).forEach((arr) =>
    arr.sort((a, b) => (a.riskScore ?? 99) - (b.riskScore ?? 99))
  );
  return groups;
};

const FILTER_CHIP_STYLE = {
  TAKE:   { active: '#16a34a', text: '#15803d' },  // green
  REDUCE: { active: '#ca8a04', text: '#a16207' },  // yellow
  SKIP:   { active: '#dc2626', text: '#b91c1c' },  // red
  All:    { active: '#6b7280', text: '#374151' },  // gray
};

// ============================================================
// Context Strip helpers (added 2026-06-07)
// Computes the regime + AI-behavior + picks-composition summary
// shown above the Risk Assessment table so the user has full
// market context at the decision point without scrolling.
// ============================================================

// Market cap → tier mapping. Uses real marketCap from Polygon
// ticker_details (already on each stock object via PolygonDataService).
// Backend pre-filters < $2B so small/micro tiers are rarely seen in
// practice — the tiering matches industry standard so the logic is
// correct if/when the filter is bypassed.
const getCapTier = (marketCap) => {
  if (!marketCap || marketCap <= 0) return null;
  if (marketCap >= 200_000_000_000) return 'mega';   // $200B+
  if (marketCap >= 10_000_000_000)  return 'large';  // $10-200B
  if (marketCap >= 2_000_000_000)   return 'mid';    // $2-10B
  if (marketCap >= 300_000_000)     return 'small';  // $300M-$2B (industry: small-cap)
  return 'micro';                                    // <$300M (industry: micro/nano)
};

const CAP_TIER_LABELS = {
  mega:  { emoji: '🛡️', name: 'Mega',  desc: '$200B+' },
  large: { emoji: '💼', name: 'Large', desc: '$10-200B' },
  mid:   { emoji: '📈', name: 'Mid',   desc: '$2-10B' },
  small: { emoji: '🎲', name: 'Small', desc: '$300M-$2B' },
  micro: { emoji: '🔬', name: 'Micro', desc: '<$300M' },
};

// SIC code (4-digit numeric, 1972 US standard) → coarse sector
// category. Numeric range matching replaces the old sic_description
// substring keywords: ranges are exhaustive over the SIC space, so
// every code lands in a bucket — 'other' only remains for missing /
// unparseable codes. More-specific sub-ranges (e.g. 2830-2836 pharma)
// must be checked BEFORE their containing range (2800-2899 chemicals).
const getSectorCategory = (sicCode) => {
  if (!sicCode) return null;
  const c = parseInt(sicCode, 10);
  if (isNaN(c)) return null;

  // Agriculture / Forestry / Fishing — food production
  if (c >= 100 && c <= 999) return 'staples';

  // Mining
  if (c >= 1300 && c <= 1399) return 'energy';      // Oil & gas extraction
  if (c >= 1000 && c <= 1499) return 'industrial';  // Metal/coal/nonmetallic mining
  // Construction
  if (c >= 1500 && c <= 1799) return 'industrial';

  // Manufacturing
  if (c >= 2000 && c <= 2199) return 'staples';     // Food & tobacco
  if (c >= 2200 && c <= 2399) return 'consumer';    // Textiles & apparel
  if (c >= 2400 && c <= 2499) return 'industrial';  // Lumber & wood
  if (c >= 2500 && c <= 2599) return 'consumer';    // Furniture
  if (c >= 2600 && c <= 2799) return 'industrial';  // Paper & printing
  if (c >= 2830 && c <= 2836) return 'health';      // Pharma & biologicals
  if (c >= 2800 && c <= 2899) return 'industrial';  // Other chemicals
  if (c >= 2900 && c <= 2999) return 'energy';      // Petroleum refining
  if (c >= 3000 && c <= 3199) return 'consumer';    // Rubber/plastics/leather goods
  if (c >= 3200 && c <= 3299) return 'industrial';  // Stone, clay, glass
  if (c >= 3300 && c <= 3499) return 'industrial';  // Metals
  if (c >= 3570 && c <= 3579) return 'tech';        // Computer & office equipment
  if (c >= 3500 && c <= 3599) return 'industrial';  // Industrial machinery
  if (c >= 3670 && c <= 3679) return 'tech';        // Semiconductors & components
  if (c >= 3600 && c <= 3699) return 'industrial';  // Other electrical equipment
  if (c >= 3700 && c <= 3799) return 'industrial';  // Transportation equipment
  if (c >= 3840 && c <= 3851) return 'health';      // Medical/dental instruments
  if (c >= 3800 && c <= 3899) return 'industrial';  // Other instruments
  if (c >= 3900 && c <= 3999) return 'consumer';    // Misc manufacturing (toys, jewelry)

  // Transportation / Communications / Utilities
  if (c >= 4600 && c <= 4699) return 'energy';      // Pipelines
  if (c >= 4800 && c <= 4899) return 'telecom';
  if (c >= 4900 && c <= 4999) return 'utility';
  if (c >= 4000 && c <= 4799) return 'industrial';  // Rail/truck/air/shipping

  // Wholesale / Retail
  if (c >= 5400 && c <= 5499) return 'staples';     // Grocery & food stores
  if (c === 5912)             return 'health';      // Drug stores
  if (c >= 5000 && c <= 5999) return 'consumer';    // All other wholesale/retail

  // Finance / Insurance / Real Estate
  if (c >= 6500 && c <= 6599) return 'reit';        // Real estate
  if (c === 6798)             return 'reit';        // REITs proper
  if (c >= 6000 && c <= 6799) return 'finance';

  // Services
  if (c >= 7370 && c <= 7379) return 'tech';        // Computer services & software
  if (c >= 8000 && c <= 8099) return 'health';      // Health services
  if (c >= 8730 && c <= 8739) return 'tech';        // Research labs (incl. biotech R&D)
  if (c >= 7000 && c <= 8999) return 'consumer';    // Hotels/amusement/legal/education

  return 'other';
};

// Strip common SIC prefixes ("RETAIL-", "WHOLESALE-", "SERVICES-"),
// title-case word-by-word (preserving connectors like &), and truncate
// at a word boundary near 30 chars. Matches what the left-side stock
// list shows so Card/Modal subline reads the same raw text as the rest
// of the UI: "RUBBER & PLASTICS FOOTWEAR" → "Rubber & Plastics Footwear",
// "STEEL WORKS, BLAST FURNACES & ROLLING MILLS" → "Steel Works, Blast
// Furnaces..".
const abbreviateSic = (sic) => {
  if (!sic) return '';
  const cleaned = sic.replace(/^(RETAIL-|WHOLESALE-|SERVICES-)/i, '').trim();
  // Title case word-by-word, leaving inter-word whitespace intact.
  const titled = cleaned
    .split(/(\s+)/)
    .map((part) =>
      /^\s+$/.test(part)
        ? part
        : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    )
    .join('');
  if (titled.length <= 30) return titled;
  // Truncate at word boundary near 28 chars + ".."
  const truncated = titled.slice(0, 28);
  const lastSpace = truncated.lastIndexOf(' ');
  const base = lastSpace > 12 ? truncated.slice(0, lastSpace) : truncated;
  return base + '..';
};

// Sector chip for the Card subline / Modal header — always returns the
// raw SIC description (abbreviated) for the name so the chip matches
// what the left-side stock list shows for the same ticker. Emoji is
// borrowed from the numeric-SIC-code category; for stocks missing a
// sic_code the generic 🏷️ marker stands in.
// Context Strip Row 2 (mix display) intentionally does NOT use this —
// it stays on category buckets because the percentage breakdown needs
// to group, not enumerate.
const getSectorChip = (sicCode, sicDescription) => {
  if (!sicDescription) return null;
  const cat = getSectorCategory(sicCode);
  const emoji = (cat && cat !== 'other') ? SECTOR_LABELS[cat].emoji : '🏷️';
  return { emoji, name: abbreviateSic(sicDescription) };
};

const SECTOR_LABELS = {
  tech:       { emoji: '🖥️', name: 'Tech' },
  health:     { emoji: '🏥', name: 'Health' },
  reit:       { emoji: '🏢', name: 'REIT' },
  finance:    { emoji: '💰', name: 'Finance' },
  energy:     { emoji: '⚡', name: 'Energy' },
  staples:    { emoji: '🛒', name: 'Essentials' },
  consumer:   { emoji: '🛍️', name: 'Consumer' },
  utility:    { emoji: '🔌', name: 'Utility' },
  telecom:    { emoji: '📡', name: 'Telecom' },
  industrial: { emoji: '🏭', name: 'Industrial' },
  other:      { emoji: '❓', name: 'Other' },
};

// AI count → color tier. Reflects AI confidence: more picks = more
// opportunities = green. Fewer picks = Abstain triggered = AI saw few
// clean setups = red.
//
// Claude/GPT thresholds (max usually 8 per AI):
//   🟢 ≥ 7   confident
//   🟡 5-6   normal
//   🔴 ≤ 4   cautious / Abstain triggered
//
// Both Recommend thresholds (consensus, max = min(claude, gpt)):
//   🟢 ≥ 5   high consensus
//   🟡 2-4   moderate
//   🔴 ≤ 1   very low / no consensus
const getAiCountColor = (count, isBoth = false) => {
  if (count == null) return '#9ca3af';
  if (isBoth) {
    if (count >= 5) return '#16a34a'; // green
    if (count >= 2) return '#ca8a04'; // yellow
    return '#dc2626';                 // red
  }
  if (count >= 7) return '#16a34a';
  if (count >= 5) return '#ca8a04';
  return '#dc2626';
};

const getAiCountEmoji = (count, isBoth = false) => {
  if (count == null) return '⚪';
  if (isBoth) {
    if (count >= 5) return '🟢';
    if (count >= 2) return '🟡';
    return '🔴';
  }
  if (count >= 7) return '🟢';
  if (count >= 5) return '🟡';
  return '🔴';
};

// Compute distribution of picks across cap tiers / sectors.
// Returns array of { key, emoji, name, count, pct } sorted by count desc.
const computeMix = (picks, stockMap, getKey, labelMap) => {
  if (!picks || picks.length === 0) return [];
  const counts = {};
  picks.forEach((p) => {
    const stock = stockMap[p.symbol];
    if (!stock) return;
    const key = getKey(stock);
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  const total = Object.values(counts).reduce((sum, c) => sum + c, 0);
  if (total === 0) return [];
  return Object.entries(counts)
    .map(([key, count]) => ({
      key,
      ...labelMap[key],
      count,
      pct: Math.round((count / total) * 100),
    }))
    .sort((a, b) => b.count - a.count);
};

// Column header tooltips with day-trading interpretation tables. Multi-line
// via \n — works in Chrome/Firefox/Safari for native HTML title attribute.
const COL_TOOLTIPS = {
  sym:     'Stock symbol. ✓ = both AIs agree',
  score:   'LLM risk score (1=lowest risk, 10=highest)\n\nLevels (Pattern C):\n  🟢 1-3:  LOW - clean setup, often consensus\n  🟡 4-6:  MEDIUM - some concerns but tradeable\n  🔴 7-10: HIGH - significant risks, often SKIP',
  rr:      'Reward-to-Risk ratio\n= (Target − Entry) / (Entry − Stop)\n\nDay trading interpretation:\n  🔴 < 1:1:    poor (risk exceeds reward)\n  🟡 1-2:1:    acceptable, standard day trade\n  🟢 ≥ 2:1:    good',
  riskPct: 'Stop-Entry % — Stop\'s distance from Entry\n= (Entry − Stop) / Entry × 100\n\nAlso the per-share loss % if the stop hits.\nDay trading interpretation:\n  🟡 < 2%:   tight stop (ordinary noise can whipsaw it)\n  🟢 2-4%:   moderate (typical day trade)\n  🟡 4-7%:   wide (large single-trade loss)\n  🔴 > 7%:   too wide (one trade should not lose this much)',
  pos:     'Dollar position size for this stock\n(per-stock customization — Commit 4)',
  shares:  'Computed shares = Position $ / Entry (rounded down)',
  maxLoss: 'Max dollar loss if stopped\n= Shares × per-share risk',
  rvol:    'Relative Volume = today\'s volume / 20-day avg\n\nDay trading interpretation:\n  🔴 < 1x:    below average\n  🟡 1-1.5x:  average\n  🟢 1.5-2.5x: good\n  ⚡ ≥ 2.5x:  heavy volume, momentum signal',
  gap:     'Open vs prev close % = (Open − PrevClose) / PrevClose × 100\n\nDay trading interpretation:\n  🟢 |Gap| < 2%:     normal\n  🟡 |Gap| 2-5%:     moderate\n  🔴 |Gap| 5-10%:    large (gap-fill risk)\n  🚨 |Gap| > 10%:    extreme',
  vwap:    'Stop-VWAP % — Stop\'s position vs VWAP\n= (Stop − VWAP) / VWAP × 100\n\nSign matters (for LONG):\n  🟢 ≤ -3%:    Stop well below VWAP (usually safe, room for a VWAP test)\n  🟡 -3 ~ -1%: Stop below VWAP but close (a VWAP test may trigger it)\n  🔴 -1 ~ +1%: Stop hugging VWAP (algo sweep zone)\n  🟡 +1 ~ +3%: Stop tight above VWAP (tight setup, ordinary noise can trigger it)\n  🟢 ≥ +3%:    Stop well above VWAP (aggressive setup, VWAP whipsaw does not apply)',
  recom:   'LLM recommendation\n  TAKE (green)\n  REDUCE SIZE (yellow)\n  SKIP (red)',
};

// Quick lookup: symbol → its action string for each AI. Used by table cells
// to derive R:R / Risk % / Max loss on demand.
const buildActionMap = (result) => {
  const map = {};
  result?.topPicks?.forEach((p) => {
    if (p?.symbol) map[p.symbol] = p.action;
  });
  return map;
};

// Recommendation badge color (matches Top Picks / Detailed cards)
const recStyleFor = (rec) => {
  switch (rec) {
    case 'TAKE':        return { bg: '#dcfce7', color: '#166534' };
    case 'REDUCE SIZE': return { bg: '#fef3c7', color: '#92400e' };
    case 'SKIP':        return { bg: '#fee2e2', color: '#991b1b' };
    default:            return { bg: '#f3f4f6', color: '#374151' };
  }
};

// ============================================================
// Pattern C: unified 🟢🟡🔴 traffic-light icons across all
// risk-related columns (Risk Score / R:R / Loss % / RVOL /
// Gap % / Stop-VWAP %). Two special icons stack on top of the
// red tier for RVOL (⚡ momentum) and Gap % (🚨 extreme).
// Each helper returns { text, color, fontWeight, icon } so the
// table cell can render number-then-icon (or icon-then-number
// for Risk Score, which uses the icon as a category badge).
// ============================================================

// Risk Score: icon LEFT (category badge for LOW/MED/HIGH)
const riskScoreDisplay = (score) => {
  if (score == null) return { text: '—', icon: null };
  const icon = score <= 3 ? '🟢' : score <= 6 ? '🟡' : '🔴';
  return {
    text: `${score}/10`,
    icon,
    color: RISK_SCORE_COLOR(score),
    fontWeight: '600',
  };
};

// R:R: icon RIGHT
const rrDisplay = (rr) => {
  if (rr == null) return { text: '—', icon: null };
  const icon = rr >= RR_THRESHOLDS.good ? '🟢' :
               rr >= RR_THRESHOLDS.poor ? '🟡' : '🔴';
  const color = rr >= RR_THRESHOLDS.good ? '#16a34a' :
                rr >= RR_THRESHOLDS.poor ? '#ca8a04' : '#dc2626';
  return { text: `${rr.toFixed(2)}:1`, icon, color };
};

// Loss % (formerly Risk %): icon RIGHT.
// Per-share loss % if stop hit. Sweet spot 2-4%; both extremes (too
// tight whipsaw / too wide) get yellow; > 7% gets red.
const lossPctDisplay = (pct) => {
  if (pct == null) return { text: '—', icon: null };
  const tight = pct < LOSS_PCT_THRESHOLDS.tight;
  const wide  = pct >= LOSS_PCT_THRESHOLDS.wide;
  const overWide = pct > LOSS_PCT_THRESHOLDS.wide;
  const inSafeBand = pct >= LOSS_PCT_THRESHOLDS.tight && pct < LOSS_PCT_THRESHOLDS.safe;
  let icon, color;
  if (overWide)         { icon = '🔴'; color = '#dc2626'; }
  else if (inSafeBand)  { icon = '🟢'; color = '#16a34a'; }
  else                  { icon = '🟡'; color = '#ca8a04'; }
  return { text: `${pct.toFixed(1)}%`, icon, color };
};

const rvolDisplay = (rvol) => {
  if (rvol == null) return { text: '—', icon: null };
  // Momentum bonus (⚡) layered on top of the green tier
  if (rvol >= RVOL_HIGH_THRESHOLD) {
    return { text: `${rvol.toFixed(1)}x`, icon: '⚡', color: '#7c3aed', fontWeight: '600' };
  }
  const icon = rvol >= RVOL_TIERS.good ? '🟢' :
               rvol >= RVOL_TIERS.low  ? '🟡' : '🔴';
  const color = rvol >= RVOL_TIERS.good ? '#16a34a' :
                rvol >= RVOL_TIERS.low  ? '#ca8a04' : '#dc2626';
  return { text: `${rvol.toFixed(1)}x`, icon, color };
};

const gapDisplay = (gap) => {
  if (gap == null) return { text: '—', icon: null };
  const abs = Math.abs(gap);
  const extreme = abs >= GAP_THRESHOLDS.extremePct;
  const large   = abs >= GAP_THRESHOLDS.warningPct;
  // 🚨 extreme overrides red — preserves the "this is exceptional" signal
  let icon, color, fontWeight = '400';
  if (extreme) { icon = '🚨'; color = '#dc2626'; fontWeight = '700'; }
  else if (large) { icon = '🔴'; color = '#dc2626'; }
  else if (abs >= 2) { icon = '🟡'; color = '#ca8a04'; }
  else { icon = '🟢'; color = '#16a34a'; }
  return { text: `${gap >= 0 ? '+' : ''}${gap.toFixed(1)}%`, icon, color, fontWeight };
};

// Stop-VWAP %: sign-aware 5-tier mapping. For LONG positions the
// direction (above vs below VWAP) carries semantic info beyond just
// distance:
//
//   ≤ -3%   : 🟢  Stop far BELOW VWAP — typical safe day-trade setup
//                  (lets price test VWAP support without stopping out)
//   -3 ~ -1%: 🟡  Stop below VWAP but close (VWAP test may trigger stop)
//   -1 ~ +1%: 🔴  Stop AT / very near VWAP (algorithmic sweep death zone)
//   +1 ~ +3%: 🟡  Stop above VWAP and tight (general-noise vulnerable)
//   ≥ +3%   : 🟢  Stop far ABOVE VWAP — aggressive tight-stop setup,
//                  VWAP whipsaw doesn't apply but stop is overall tight
//
// "Safe" in the 🟢 bands means "safe from VWAP-specific whipsaw" — not
// "safest overall trade." See COL_TOOLTIPS.vwap for the legend.
const stopVwapDisplay = (val) => {
  if (val == null) return { text: '—', icon: null };
  let icon, color;
  if (val <= -3)       { icon = '🟢'; color = '#16a34a'; }   // far below VWAP — safe
  else if (val <= -1)  { icon = '🟡'; color = '#ca8a04'; }   // below VWAP, mid
  else if (val < 1)    { icon = '🔴'; color = '#dc2626'; }   // at VWAP — death zone
  else if (val < 3)    { icon = '🟡'; color = '#ca8a04'; }   // above VWAP, tight
  else                 { icon = '🟢'; color = '#16a34a'; }   // far above VWAP — safe-but-aggressive
  return { text: `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`, icon, color };
};

const renderRiskTable = (
  risks, claudeActions, gptActions, sortBy, setSortBy,
  selectedSymbols, toggleSelected, getPosition, setPosition,
  stockMap, onSymbolClick,
) => {
  const onSort = (field) => {
    setSortBy((cur) =>
      cur.field === field
        ? { field, order: cur.order === 'asc' ? 'desc' : 'asc' }
        : { field, order: 'asc' }
    );
  };
  const sortIcon = (field) =>
    sortBy.field === field ? (sortBy.order === 'asc' ? ' ↑' : ' ↓') : '';

  const th = (label, field, tooltip, extraStyle = {}) => (
    <th
      title={tooltip}
      onClick={field ? () => onSort(field) : undefined}
      style={{
        padding: '8px 10px',
        textAlign: 'left',
        fontSize: '0.95em',
        fontWeight: '700',
        color: '#1f2937',
        borderBottom: '2px solid #d1d5db',
        backgroundColor: '#f3f4f6',
        cursor: field ? 'pointer' : 'default',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        ...extraStyle,
      }}
    >
      {label}{field && sortIcon(field)}
    </th>
  );

  return (
    <div style={{ overflowX: 'auto', marginBottom: '8px' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.95em',
      }}>
        <thead>
          <tr>
            {th('☑',            null,        'Include in trading plan (Total risk)', { width: '28px' })}
            {th('Sym',          null,        COL_TOOLTIPS.sym)}
            {th('Risk Score',   'riskScore', COL_TOOLTIPS.score)}
            {th('R:R',          'rr',        COL_TOOLTIPS.rr)}
            {th('Stop-Entry %', 'riskPct',   COL_TOOLTIPS.riskPct)}
            {th('Stop-VWAP %',  null,        COL_TOOLTIPS.vwap)}
            {th('RVOL',         null,        COL_TOOLTIPS.rvol)}
            {th('Gap %',        null,        COL_TOOLTIPS.gap)}
            {th('Pos $',        null,        COL_TOOLTIPS.pos)}
            {th('Shares',       null,        COL_TOOLTIPS.shares)}
            {th('Max $',        'maxLoss',   COL_TOOLTIPS.maxLoss)}
            {th('Recom',        null,        COL_TOOLTIPS.recom)}
          </tr>
        </thead>
        <tbody>
          {risks.map((r) => {
            const m = computeMetrics(claudeActions[r.symbol] || gptActions[r.symbol]);
            const position = getPosition(r.symbol);
            const shares = m ? computeShares(position, m.entry) : null;
            const maxLoss = m ? computeMaxLoss(position, m) : null;
            const isSelected = selectedSymbols.has(r.symbol);
            const recStyle = recStyleFor(r.recommendation);
            const td = (content, alignRight = false) => (
              <td style={{
                padding: '6px 8px',
                borderBottom: '1px solid #f3f4f6',
                textAlign: alignRight ? 'right' : 'left',
                whiteSpace: 'nowrap',
              }}>
                {content}
              </td>
            );
            return (
              <tr
                key={r.symbol}
                onClick={(e) => {
                  // Avoid opening modal when clicking the checkbox / dropdown
                  if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' ||
                      e.target.tagName === 'OPTION') return;
                  onSymbolClick?.(r.symbol);
                }}
                style={{
                  backgroundColor: isSelected ? '#fefce8' : 'white',
                  cursor: onSymbolClick ? 'pointer' : 'default',
                }}
                title="Click for full risk details"
              >
                {td(
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelected(r.symbol)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ cursor: 'pointer' }}
                  />
                )}
                {td(
                  <>
                    <span style={{ fontWeight: '600' }}>{r.symbol}</span>
                    {r.bothAIsAgree && <span style={{ color: '#16a34a', marginLeft: '4px' }}>✓</span>}
                  </>
                )}
                {(() => {
                  const d = riskScoreDisplay(r.riskScore);
                  return td(
                    <span style={{ color: d.color, fontWeight: d.fontWeight || '400' }}>
                      {d.icon && d.icon + ' '}{d.text}
                    </span>
                  );
                })()}
                {(() => {
                  const d = rrDisplay(m?.rr);
                  return td(
                    <span style={{ color: d.color, fontWeight: d.fontWeight || '400' }}>
                      {d.icon && d.icon + ' '}{d.text}
                    </span>
                  );
                })()}
                {(() => {
                  const d = lossPctDisplay(m?.riskPct);
                  return td(
                    <span style={{ color: d.color, fontWeight: d.fontWeight || '400' }}>
                      {d.icon && d.icon + ' '}{d.text}
                    </span>
                  );
                })()}
                {(() => {
                  const s = stockMap[r.symbol];
                  // Stop-VWAP distance — use Claude's stop (conservative wider) or fall back to GPT.
                  const dist = m && s?.vwap != null
                    ? computeVwapDistance(m.stop, s.vwap)
                    : null;
                  const d = stopVwapDisplay(dist);
                  return td(
                    <span style={{ color: d.color, fontWeight: d.fontWeight || '400' }}>
                      {d.icon && d.icon + ' '}{d.text}
                    </span>
                  );
                })()}
                {(() => {
                  const s = stockMap[r.symbol];
                  const d = rvolDisplay(s?.relativeVolume);
                  return td(
                    <span style={{ color: d.color, fontWeight: d.fontWeight || '400' }}>
                      {d.icon && d.icon + ' '}{d.text}
                    </span>
                  );
                })()}
                {(() => {
                  const s = stockMap[r.symbol];
                  const d = gapDisplay(s?.gapPercent);
                  return td(
                    <span style={{ color: d.color, fontWeight: d.fontWeight || '400' }}>
                      {d.icon && d.icon + ' '}{d.text}
                    </span>
                  );
                })()}
                {td(
                  <select
                    value={position}
                    onChange={(e) => setPosition(r.symbol, Number(e.target.value))}
                    style={{
                      fontSize: '0.9em',
                      padding: '2px 4px',
                      border: '1px solid #d1d5db',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                  >
                    {POSITION_DOLLARS_OPTIONS.map((p) => (
                      <option key={p} value={p}>${(p / 1000).toFixed(0)}k</option>
                    ))}
                  </select>,
                  true
                )}
                {td(shares != null ? shares.toLocaleString() : '—', true)}
                {td(maxLoss != null ? `$${Math.round(maxLoss).toLocaleString()}` : '—', true)}
                {td(
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '3px',
                    backgroundColor: recStyle.bg,
                    color: recStyle.color,
                    fontSize: '0.85em',
                    fontWeight: '600',
                  }}>
                    {r.recommendation}
                  </span>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Symbol → stockData row lookup for VWAP / RVOL / Gap / Sector cells.
const buildStockMap = (stockData) => {
  const map = {};
  (stockData || []).forEach((s) => {
    if (s?.symbol) map[s.symbol] = s;
  });
  return map;
};

const RiskAssessmentSection = ({
  riskAssessment, isLoading, expectedPickCount = 0,
  anthropicResult, openaiResult, stockData,
  onSymbolClick,
  // Context Strip props — regime info passed down from StockChartTable
  // so the user has full market context inside the panel without
  // looking back at Row 2 of the main dashboard.
  marketSentiment, trendScore, spreadClass, iwmSpyspread,
  ctxAtScan = false, // true when the values above are the scan-time snapshot (fresh or loaded scan), not the live header
}) => {
  // Filter state — default TAKE, the user's primary review category.
  const [riskFilter, setRiskFilter] = React.useState('TAKE');
  // View mode — default 'table' for fast cross-stock scan.
  const [viewMode, setViewMode] = React.useState('table');
  // Table column sort: {field, order}. Default Score ascending (best first).
  const [sortBy, setSortBy] = React.useState({ field: 'riskScore', order: 'asc' });

  // Multi-select set (cross-filter persistent) — symbols user is actually
  // planning to trade today. Total risk computes from this subset.
  const [selectedSymbols, setSelectedSymbols] = React.useState(new Set());

  // Per-stock dollar position. Missing entries fall back to default $100k.
  // Resets when scan changes (handled by parent via key prop on remount).
  const [positionDollars, setPositionDollars] = React.useState({});

  // Account size — gates the Total risk % calculation. Default $220k matches
  // typical active day trader, persists to localStorage so user-set value
  // survives reloads.
  const [accountSize, setAccountSize] = React.useState(() => {
    try {
      const saved = localStorage.getItem('accountSize');
      return saved ? Number(saved) : DEFAULT_ACCOUNT_SIZE;
    } catch {
      return DEFAULT_ACCOUNT_SIZE;
    }
  });
  React.useEffect(() => {
    try { localStorage.setItem('accountSize', String(accountSize)); } catch {}
  }, [accountSize]);

  // Resolve per-symbol action strings from each AI's topPicks once.
  const claudeActions = React.useMemo(() => buildActionMap(anthropicResult), [anthropicResult]);
  const gptActions = React.useMemo(() => buildActionMap(openaiResult), [openaiResult]);
  const stockMap = React.useMemo(() => buildStockMap(stockData), [stockData]);

  const getPosition = (sym) => positionDollars[sym] ?? DEFAULT_POSITION_DOLLARS;
  const setPosition = (sym, dollars) =>
    setPositionDollars((prev) => ({ ...prev, [sym]: dollars }));
  const toggleSelected = (sym) =>
    setSelectedSymbols((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else next.add(sym);
      return next;
    });

  // Total risk across globally selected symbols (any filter).
  const totalRisk = React.useMemo(
    () => computeTotalRisk(selectedSymbols, claudeActions, gptActions,
                          positionDollars, DEFAULT_POSITION_DOLLARS),
    [selectedSymbols, claudeActions, gptActions, positionDollars]
  );
  const totalRiskPct = accountSize > 0 ? (totalRisk / accountSize) * 100 : null;
  const totalRiskColor =
    totalRiskPct == null                              ? '#374151' :
    totalRiskPct >= TOTAL_RISK_THRESHOLDS.severePct   ? '#dc2626' :
    totalRiskPct >= TOTAL_RISK_THRESHOLDS.warningPct  ? '#ca8a04' :
                                                        '#6b7280';

  const isStreaming = isLoading || riskAssessment?._streaming;
  const stockRisksCount = riskAssessment?.stockRisks?.length || 0;
  const hasAnyPartial = !!(riskAssessment?.overallRiskSummary)
    || stockRisksCount > 0
    || (riskAssessment?.conflicts?.length || 0) > 0
    || (riskAssessment?.missedRisks?.length || 0) > 0;

  // Pre-stream wait: render nothing. The Risk status chip at the top of the
  // panel already shows "⏳ Assessing..." — a second placeholder here is
  // redundant. Section appears as soon as the first SSE event lands.
  if (isStreaming && !hasAnyPartial) return null;

  if (!riskAssessment || riskAssessment.error) return null;

  // Risk-level styling (badge + border + text colors). bg removed — see getRecBg
  // below, since per-stock card bg is now driven by recommendation, not risk level,
  // so that "same action = same color" holds across all sections.
  const getRiskColor = (level) => {
    switch(level) {
      case 'LOW': return { border: '#22c55e', text: '#166534', badge: '#22c55e' };
      case 'MEDIUM': return { border: '#eab308', text: '#854d0e', badge: '#eab308' };
      case 'HIGH': return { border: '#ef4444', text: '#991b1b', badge: '#ef4444' };
      default: return { border: '#6b7280', text: '#374151', badge: '#6b7280' };
    }
  };
  // Background color from recommendation, matching Top Picks + Detailed Pick Card.
  const getRecBg = (rec) => {
    const r = (rec || '').toUpperCase();
    if (r.includes('SKIP')) return '#fef2f2';
    if (r.includes('REDUCE')) return '#fefce8';
    if (r.includes('TAKE')) return '#f0fdf4';
    return '#f9fafb';
  };

  const getRecStyle = (rec) => {
    switch(rec) {
      case 'TAKE': return { bg: '#dcfce7', color: '#166534' };
      case 'REDUCE SIZE': return { bg: '#fef3c7', color: '#92400e' };
      case 'SKIP': return { bg: '#fee2e2', color: '#991b1b' };
      default: return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fef3c7',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '1.2em' }}>🛡️</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>Risk Assessment</span>
        {isStreaming && (
          <span style={{ color: '#9ca3af', fontSize: '0.85em' }}>
            ⏳ Analyzing risk: {stockRisksCount}{expectedPickCount > 0 ? ` of ${expectedPickCount}` : ''} picks done
          </span>
        )}
        {/* Account input + View toggle (right side of header) */}
        {stockRisksCount > 0 && (
          <div style={{
            display: 'flex',
            gap: '8px',
            marginLeft: 'auto',
            alignItems: 'center',
          }}>
            <label style={{ fontSize: '0.85em', color: '#92400e', display: 'flex', alignItems: 'center', gap: '4px' }}>
              Account:
              <input
                type="number"
                value={accountSize}
                onChange={(e) => setAccountSize(Number(e.target.value) || 0)}
                step="10000"
                min="0"
                title="Total account size used for Total risk %"
                style={{
                  width: '90px',
                  padding: '3px 6px',
                  border: '1px solid #d97706',
                  borderRadius: '4px',
                  fontSize: '0.85em',
                }}
              />
            </label>
          </div>
        )}
        {/* View toggle — table (default) vs card */}
        {stockRisksCount > 0 && (
          <div style={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
          }}>
            {[
              { key: 'table', label: '📊 Table' },
              { key: 'card',  label: '📋 Card'  },
            ].map((v) => (
              <button
                key={v.key}
                onClick={() => setViewMode(v.key)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '4px',
                  border: '1px solid #d97706',
                  backgroundColor: viewMode === v.key ? '#d97706' : 'transparent',
                  color: viewMode === v.key ? 'white' : '#92400e',
                  fontSize: '0.8em',
                  fontWeight: viewMode === v.key ? '600' : '500',
                  cursor: 'pointer',
                }}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
        {riskAssessment.inputTokens && (
          <span style={{ fontSize: '0.75em', color: '#9ca3af' }}>
            {riskAssessment.inputTokens + riskAssessment.outputTokens} tokens
          </span>
        )}
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* Overall Risk Summary */}
        {riskAssessment.overallRiskSummary && (
          <div style={{
            backgroundColor: '#fffbeb',
            padding: '10px 14px',
            borderRadius: '6px',
            marginBottom: '12px',
            borderLeft: '3px solid #f59e0b',
            fontSize: '0.95em',
            color: '#78350f',
            lineHeight: '1.5'
          }}>
            {riskAssessment.overallRiskSummary}
          </div>
        )}

        {/* Watch Out For — global / sector / market-regime warnings.
            Positioned right after overallRiskSummary as the macro frame
            the user reads *before* drilling into per-stock decisions. */}
        {riskAssessment.missedRisks?.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#b45309',
              fontSize: '0.95em'
            }}>
              Watch Out For
            </div>
            {riskAssessment.missedRisks.map((risk, i) => (
              <div key={i} style={{
                backgroundColor: '#fef3c7',
                padding: '6px 10px',
                borderRadius: '4px',
                marginBottom: '4px',
                fontSize: '0.85em',
                color: '#78350f'
              }}>
                {risk}
              </div>
            ))}
          </div>
        )}

        {/* Context Strip — regime + AI behavior + picks composition.
            Two compact rows so the user has full decision context
            without looking up at the main dashboard's Row 2. */}
        {stockRisksCount > 0 && (() => {
          const stockMap = buildStockMap(stockData);
          const claudeCount = anthropicResult?.topPicks?.length || 0;
          const gptCount    = openaiResult?.topPicks?.length || 0;
          const bothCount   = (() => {
            if (!anthropicResult?.topPicks || !openaiResult?.topPicks) return 0;
            const gptSet = new Set(openaiResult.topPicks.map(p => p.symbol));
            return anthropicResult.topPicks.filter(p => gptSet.has(p.symbol)).length;
          })();
          const picks = riskAssessment?.stockRisks || [];
          const capMix    = computeMix(picks, stockMap, (s) => getCapTier(s.marketCap), CAP_TIER_LABELS);
          const sectorMix = computeMix(picks, stockMap, (s) => getSectorCategory(s.sicCode), SECTOR_LABELS);
          return (
            <div style={{
              backgroundColor: '#fafafa',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              padding: '8px 14px',
              marginBottom: '8px',
              fontSize: '0.85em',
            }}>
              {/* Row 1: sentiment + spread + AI counts.
                  Sentiment/spread use the same helpers as the dashboard's
                  Row 2 (marketUtils.js) for pixel-level visual consistency.
                  AI counts are fully tier-colored (emoji + label + count),
                  using | separators to match the Row 2 segmentation style. */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                {marketSentiment && marketSentiment !== 'error' && marketSentiment !== 'loading' && (
                  <span
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'help' }}
                    title={
                      'Market trend score — weighted across S&P 500 / Nasdaq / Russell 2000 / Dow.\n' +
                      '🟢 Bullish:  ≥ +0.30%\n' +
                      '🟡 Mixed:    -0.30% ~ +0.30%\n' +
                      '🔴 Bearish:  ≤ -0.30%'
                    }
                  >
                    {ctxAtScan && <span style={{ color: '#6b7280' }}>At scan ·</span>}
                    <span style={{ fontSize: '1.1em' }}>{getSentimentEmoji(marketSentiment)}</span>
                    <span style={{ fontWeight: 'bold', color: getSentimentColor(marketSentiment) }}>
                      {formatMarketSentiment(trendScore, marketSentiment)}
                    </span>
                  </span>
                )}
                {spreadClass && (() => {
                  const colors = getSpreadColor(spreadClass);
                  return (
                    <>
                      <span style={{ color: '#d1d5db' }}>|</span>
                      <span
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'help' }}
                        title={
                          'Russell 2000 (small-cap) vs S&P 500 (large-cap) — daily relative performance.\n' +
                          '🚨 severe-narrow     (≤ -0.50%): strong risk-off — money fleeing to large caps\n' +
                          '⚠️ mild-narrow       (-0.50% ~ -0.20%): mild risk-off\n' +
                          '📊 balanced          (-0.20% ~ +0.20%): no rotation signal\n' +
                          '📈 small-cap-leading (≥ +0.20%): risk-on — small caps outperforming'
                        }
                      >
                        <span>{getSpreadEmoji(spreadClass)}</span>
                        <span style={{ fontWeight: '500', color: colors.text }}>
                          {spreadClass}
                        </span>
                        {iwmSpyspread != null && (
                          <span style={{ fontWeight: 'bold', color: colors.text }}>
                            {iwmSpyspread >= 0 ? '+' : ''}{iwmSpyspread.toFixed(2)}%
                          </span>
                        )}
                      </span>
                    </>
                  );
                })()}
                {(claudeCount > 0 || gptCount > 0) && (
                  <>
                    <span style={{ color: '#d1d5db' }}>|</span>
                    <span
                      style={{ color: getAiCountColor(claudeCount), fontWeight: 'bold' }}
                      title="Claude top picks. 🟢 ≥7 confident / 🟡 5-6 normal / 🔴 ≤4 cautious (Abstain triggered)"
                    >
                      {getAiCountEmoji(claudeCount)} Claude {claudeCount}
                    </span>
                    <span style={{ color: '#d1d5db' }}>|</span>
                    <span
                      style={{ color: getAiCountColor(gptCount), fontWeight: 'bold' }}
                      title="GPT top picks. Same tiers as Claude."
                    >
                      {getAiCountEmoji(gptCount)} GPT {gptCount}
                    </span>
                    <span style={{ color: '#d1d5db' }}>|</span>
                    <span
                      style={{ color: getAiCountColor(bothCount, true), fontWeight: 'bold' }}
                      title="Both AIs agreed. 🟢 ≥5 high consensus / 🟡 2-4 moderate / 🔴 ≤1 low consensus"
                    >
                      {getAiCountEmoji(bothCount, true)} Both {bothCount}
                    </span>
                  </>
                )}
              </div>
              {/* Row 2: Cap mix + Sector mix */}
              {(capMix.length > 0 || sectorMix.length > 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px', color: '#4b5563' }}>
                  {capMix.length > 0 && (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span
                        style={{ color: '#6b7280', fontSize: '0.92em' }}
                        title={
                          'Market cap tiers (industry standard):\n' +
                          '🛡️ Mega   ($200B+):     largest, lowest volatility\n' +
                          '💼 Large  ($10B-$200B): blue chips\n' +
                          '📈 Mid    ($2B-$10B):   growth + reasonable liquidity\n' +
                          '🎲 Small  ($300M-$2B):  higher volatility, more idiosyncratic\n' +
                          '🔬 Micro  (<$300M):     very high volatility, low liquidity'
                        }
                      >Cap:</span>
                      {capMix.map((m) => (
                        <span
                          key={m.key}
                          title={`${m.name} cap: ${CAP_TIER_LABELS[m.key]?.desc || ''}`}
                        >
                          {m.emoji} <strong>{m.name}</strong> {m.pct}%
                        </span>
                      ))}
                    </div>
                  )}
                  {sectorMix.length > 0 && (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ color: '#6b7280', fontSize: '0.92em' }}>Sector:</span>
                      {sectorMix.map((m) => (
                        <span key={m.key}>{m.emoji} <strong>{m.name}</strong> {m.pct}%</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Summary chip row — grouped by recommendation, sorted by riskScore */}
        {stockRisksCount > 0 && (() => {
          const groups = groupAndSortRisks(riskAssessment.stockRisks);
          const groupConfig = [
            { key: 'TAKE',   label: '🟢 TAKE',   stocks: groups.TAKE },
            { key: 'REDUCE', label: '🟡 REDUCE', stocks: groups.REDUCE },
            { key: 'SKIP',   label: '🔴 SKIP',   stocks: groups.SKIP },
          ].filter((g) => g.stocks.length > 0);
          if (groupConfig.length === 0) return null;
          return (
            <div style={{
              backgroundColor: '#f9fafb',
              padding: '10px 14px',
              borderRadius: '6px',
              marginBottom: '12px',
              border: '1px solid #e5e7eb',
              fontSize: '0.85em',
            }}>
              <div style={{ color: '#6b7280', marginBottom: '6px', fontSize: '0.9em' }}>
                Summary by category (sorted by riskScore within each group):
              </div>
              {groupConfig.map((g) => (
                <div key={g.key} style={{ marginBottom: '4px', color: '#374151' }}>
                  <span style={{ fontWeight: '600' }}>{g.label} ({g.stocks.length}):</span>{' '}
                  {g.stocks.map((s, i) => (
                    <React.Fragment key={s.symbol}>
                      {i > 0 && <span style={{ color: '#9ca3af' }}> → </span>}
                      <span
                        onClick={() => onSymbolClick?.(s.symbol)}
                        style={{
                          cursor: onSymbolClick ? 'pointer' : 'default',
                          textDecoration: onSymbolClick ? 'underline dotted' : 'none',
                          textDecorationColor: '#9ca3af',
                          textUnderlineOffset: '3px',
                        }}
                        title="View risk assessment"
                      >
                        {s.symbol}{' '}
                        <span
                          style={{
                            color: RISK_SCORE_COLOR(s.riskScore),
                            fontWeight: '600',
                          }}
                        >
                          {s.riskScore}/10
                        </span>
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Filter chips — X/Y count (selected/total per filter) + Total risk */}
        {stockRisksCount > 0 && (() => {
          const groups = groupAndSortRisks(riskAssessment.stockRisks);
          // Count selected within each group
          const countSelected = (stocks) =>
            stocks.reduce((n, s) => n + (selectedSymbols.has(s.symbol) ? 1 : 0), 0);
          const allSelectedCount = selectedSymbols.size;
          const chips = [
            { key: 'All',    label: 'All',         total: stockRisksCount,     selected: allSelectedCount },
            { key: 'TAKE',   label: 'TAKE',        total: groups.TAKE.length,   selected: countSelected(groups.TAKE) },
            { key: 'REDUCE', label: 'REDUCE SIZE', total: groups.REDUCE.length, selected: countSelected(groups.REDUCE) },
            { key: 'SKIP',   label: 'SKIP',        total: groups.SKIP.length,   selected: countSelected(groups.SKIP) },
          ];
          return (
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '12px',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {chips.map((c) => {
                  const active = riskFilter === c.key;
                  const style = FILTER_CHIP_STYLE[c.key];
                  return (
                    <button
                      key={c.key}
                      onClick={() => setRiskFilter(c.key)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '14px',
                        border: `1px solid ${active ? style.active : '#d1d5db'}`,
                        backgroundColor: active ? style.active : 'transparent',
                        color: active ? 'white' : style.text,
                        fontSize: '0.85em',
                        fontWeight: active ? '600' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {active && '✓ '}{c.label} ({c.selected}/{c.total})
                    </button>
                  );
                })}
              </div>
              {/* Total risk indicator (cross-filter selectedSymbols) */}
              {selectedSymbols.size > 0 && (
                <div style={{
                  fontSize: '0.85em',
                  color: totalRiskColor,
                  fontWeight: totalRiskPct >= TOTAL_RISK_THRESHOLDS.severePct ? '700' : '600',
                  whiteSpace: 'nowrap',
                }}>
                  {totalRiskPct >= TOTAL_RISK_THRESHOLDS.severePct && '🚨 '}
                  {totalRiskPct >= TOTAL_RISK_THRESHOLDS.warningPct &&
                   totalRiskPct < TOTAL_RISK_THRESHOLDS.severePct && '⚠️ '}
                  Total risk: ${Math.round(totalRisk).toLocaleString()}
                  {totalRiskPct != null && ` (${totalRiskPct.toFixed(2)}%)`}
                </div>
              )}
            </div>
          );
        })()}

        {/* Per-Stock Risks — Table view (default) or Card view, both
            filtered by riskFilter and sorted by sortBy. */}
        {(() => {
          const filtered = (riskAssessment.stockRisks || []).filter((r) => {
            if (riskFilter === 'All') return true;
            return RECOMMENDATION_FILTERS[r.recommendation] === riskFilter;
          });

          // Sort according to the active sortBy. Supports score / R:R /
          // Risk % / Max $. Empty / null values sink to the bottom.
          const getSortValue = (r, field) => {
            if (field === 'riskScore') return r.riskScore ?? 99;
            const m = computeMetrics(claudeActions[r.symbol] || gptActions[r.symbol]);
            if (!m) return field === 'rr' ? -1 : 999;  // sink
            if (field === 'rr') return m.rr;
            if (field === 'riskPct') return m.riskPct;
            if (field === 'maxLoss') {
              return computeMaxLoss(DEFAULT_POSITION_DOLLARS, m) ?? 999999;
            }
            return 0;
          };
          const sorted = [...filtered].sort((a, b) => {
            const av = getSortValue(a, sortBy.field);
            const bv = getSortValue(b, sortBy.field);
            return sortBy.order === 'asc' ? av - bv : bv - av;
          });

          if (filtered.length === 0 && stockRisksCount > 0) {
            return (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '0.9em',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
              }}>
                No stocks in this category. Try a different filter above.
              </div>
            );
          }

          if (viewMode === 'table') {
            return renderRiskTable(
              sorted, claudeActions, gptActions, sortBy, setSortBy,
              selectedSymbols, toggleSelected, getPosition, setPosition,
              stockMap, onSymbolClick,
            );
          }

          return sorted.map((risk) => {
          const riskColors = getRiskColor(risk.riskLevel);
          const recStyle = getRecStyle(risk.recommendation);
          return (
            <div key={risk.symbol} style={{
              backgroundColor: getRecBg(risk.recommendation),
              padding: '10px 12px',
              borderRadius: '6px',
              marginBottom: '8px',
              borderLeft: `3px solid ${riskColors.border}`
            }}>
              {/* Symbol + Risk Badge + Recommendation Badge */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '6px',
                flexWrap: 'wrap',
                gap: '4px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '1em' }}>{risk.symbol}</span>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '0.75em',
                    fontWeight: '600',
                    backgroundColor: riskColors.badge,
                    color: 'white'
                  }}>
                    {risk.riskLevel} ({risk.riskScore}/10)
                  </span>
                  {risk.bothAIsAgree && (
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '0.75em',
                      backgroundColor: '#dcfce7',
                      color: '#166534'
                    }}>
                      Both AIs agree
                    </span>
                  )}
                </div>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '3px',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  backgroundColor: recStyle.bg,
                  color: recStyle.color
                }}>
                  {risk.recommendation}
                </span>
              </div>

              {/* Risk Factors */}
              {risk.riskFactors?.length > 0 && (
                <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px' }}>
                  {risk.riskFactors.map((factor, i) => (
                    <div key={i} style={{ marginBottom: '2px' }}>- {factor}</div>
                  ))}
                </div>
              )}

              {/* Rationale */}
              {risk.rationale && (
                <div style={{
                  marginTop: '6px',
                  fontSize: '0.85em',
                  color: riskColors.text,
                  fontStyle: 'italic'
                }}>
                  {risk.rationale}
                </div>
              )}
            </div>
          );
        });
        })()}

        {/* Conflict Alerts */}
        {riskAssessment.conflicts?.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#dc2626',
              fontSize: '0.95em'
            }}>
              Conflicting Recommendations
            </div>
            {riskAssessment.conflicts.map((conflict, i) => (
              <div key={i} style={{
                backgroundColor: '#fef2f2',
                padding: '8px 12px',
                borderRadius: '6px',
                marginBottom: '6px',
                borderLeft: '3px solid #dc2626',
                fontSize: '0.85em'
              }}>
                <div style={{ fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                  {conflict.symbol}
                </div>
                <div style={{ color: '#7c3aed' }}>Claude: {conflict.claudeView}</div>
                <div style={{ color: '#059669' }}>GPT: {conflict.gptView}</div>
                {conflict.riskImplication && (
                  <div style={{ color: '#6b7280', marginTop: '4px' }}>
                    {conflict.riskImplication}
                  </div>
                )}
                <div style={{
                  marginTop: '4px',
                  color: '#374151',
                  fontStyle: 'italic'
                }}>
                  {conflict.resolution}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Missed Risks */}
      </div>
    </div>
  );
};

// Format scan time for display (24-hour format with seconds to match market time)
const formatScanTime = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: ET_TIMEZONE
  }) + ' ET';
};

// Stored scans carry an ET wall-clock timestamp without a zone ("2026-09-16T09:47:39.6"): show HH:MM ET.
const formatStoredScanTime = (timestamp) => {
  if (!timestamp || typeof timestamp !== 'string' || timestamp.length < 16) return '';
  return timestamp.slice(11, 16) + ' ET';
};

// Main AI Analysis Panel
const AiAnalysisPanel = ({
  isOpen,
  onClose,
  anthropicResult,
  openaiResult,
  isAnthropicLoading = false,
  isOpenaiLoading = false,
  onAnalyze,
  scanTime,
  width = 600,
  stockCountOptions = [100, 120, 150, 200],
  stockCount = 100,
  onStockCountChange,
  riskAssessment = null,
  isRiskLoading = false,
  onSaveScan,
  isScanSaved = false,
  stockData = null,
  marketSentiment: marketSentimentProp = null,
  trendScore: trendScoreProp = null,
  spreadClass: spreadClassProp = null,
  iwmSpyspread = null,
  savedScans = [],
  isLoadingSavedScans = false,
  onFetchSavedScans,
  onLoadSavedScan,
  loadedScan = null,
  scanCtx = null, // market context frozen when the current fresh scan was launched
}) => {
  // A stored scan carries its own market context; a fresh scan carries the context frozen at launch.
  // Either way the panel shows the scan-time regime, never the drifting live header value.
  const loadedCtx = loadedScan?.marketContext || null;
  const ctx = loadedCtx || scanCtx;
  const ctxAtScan = Boolean(ctx);
  const marketSentiment = ctx?.marketSentiment ?? marketSentimentProp;
  const trendScore = ctx?.trendScore ?? trendScoreProp;
  const spreadClass = ctx?.spreadClass ?? spreadClassProp;
  const [isSavedScanMenuOpen, setIsSavedScanMenuOpen] = useState(false);
  const savedScanMenuRef = useRef(null);
  useEffect(() => {
    if (!isSavedScanMenuOpen) return undefined;
    const onDown = (event) => {
      if (savedScanMenuRef.current && !savedScanMenuRef.current.contains(event.target)) setIsSavedScanMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isSavedScanMenuOpen]);
  const [isStockCountMenuOpen, setIsStockCountMenuOpen] = useState(false);
  const [customCount, setCustomCount] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Risk-deep-dive modal — opens when user clicks a symbol/row from
  // anywhere in the panel (7 trigger points). Closes via ESC / backdrop /
  // X button. Always lives at panel level so any descendant can open it.
  const [selectedRiskSymbol, setSelectedRiskSymbol] = useState(null);
  const openRiskModal = useCallback((symbol) => setSelectedRiskSymbol(symbol), []);
  const closeRiskModal = useCallback(() => setSelectedRiskSymbol(null), []);

  // Maps shared with the Risk Assessment table — built once at panel
  // level so the modal can show Action Plan + Cap/Sector + per-metric
  // tier emoji using the same source data as the table view.
  const modalClaudeActions = React.useMemo(() => buildActionMap(anthropicResult), [anthropicResult]);
  const modalGptActions = React.useMemo(() => buildActionMap(openaiResult), [openaiResult]);
  const modalStockMap = React.useMemo(() => buildStockMap(stockData), [stockData]);
  useEffect(() => {
    if (!selectedRiskSymbol) return;
    const onKey = (e) => e.key === 'Escape' && closeRiskModal();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedRiskSymbol, closeRiskModal]);
  const selectedRisk = selectedRiskSymbol
    ? riskAssessment?.stockRisks?.find((r) => r.symbol === selectedRiskSymbol)
    : null;
  const dropdownRef = useRef(null);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStockCountMenuOpen(false);
      }
    };

    if (isStockCountMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStockCountMenuOpen]);

  if (!isOpen) return null;

  const isLoading = isAnthropicLoading || isOpenaiLoading || isRiskLoading;
  const hasResults = anthropicResult || openaiResult;
  const hasBothResults = anthropicResult && openaiResult && !anthropicResult.error && !openaiResult.error;
  // Progressive display: show Top Picks / Detailed Picks as soon as one AI returns successfully
  const hasAnyResult = (anthropicResult && !anthropicResult.error) || (openaiResult && !openaiResult.error);

  const handleStockCountSelect = (count) => {
    if (count === 'custom') {
      setShowCustomInput(true);
      setCustomCount(stockCount.toString());
    } else {
      onStockCountChange(count);
      setShowCustomInput(false);
    }
    setIsStockCountMenuOpen(false);
  };

  const handleCustomCountSubmit = () => {
    const count = parseInt(customCount, 10);
    if (count >= 50 && count <= 500) {
      onStockCountChange(count);
      setShowCustomInput(false);
    }
  };

  return (
    <div style={{
      width: `${width}px`,
      minWidth: '300px',
      borderLeft: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontSize: '0.95em'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1.3em' }}>🤖</span>
          <span style={{ fontWeight: '600', fontSize: '1.1em', color: 'white' }}>AI Scan</span>
          {scanTime && (
            <span style={{
              fontSize: '0.9em',
              color: 'rgba(255,255,255,0.9)',
              marginLeft: '8px',
              fontWeight: '500'
            }}>
              {formatScanTime(scanTime)}
            </span>
          )}
          {loadedScan && (
            <span style={{
              fontSize: '0.8em',
              color: 'white',
              background: 'rgba(255,255,255,0.22)',
              borderRadius: '999px',
              padding: '2px 8px',
              marginLeft: '6px'
            }}>
              📂 Loaded {loadedScan.source === 'auto' ? 'auto' : 'manual'} scan · read-only
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Stored scans of the ET day: load read-only, no AI call */}
          <div ref={savedScanMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => {
                const next = !isSavedScanMenuOpen;
                setIsSavedScanMenuOpen(next);
                if (next && onFetchSavedScans) onFetchSavedScans();
              }}
              title="Load a stored scan from today (auto cron scans or saved manual scans). Read-only, no AI cost."
              style={{
                padding: '6px 10px',
                fontSize: '0.85em',
                backgroundColor: isSavedScanMenuOpen ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              📂 Today's Scans
            </button>
            {isSavedScanMenuOpen && (
              <div style={{
                position: 'absolute',
                right: 0,
                top: '110%',
                zIndex: 30,
                background: 'white',
                color: '#1f2937',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                minWidth: '320px',
                padding: '6px',
                fontSize: '0.85em',
                textAlign: 'left'
              }}>
                <div style={{ padding: '4px 8px', color: '#6b7280' }}>Stored scans today · loads read-only, no AI cost</div>
                {isLoadingSavedScans && <div style={{ padding: '6px 8px' }}>⏳ Loading…</div>}
                {!isLoadingSavedScans && savedScans.length === 0 && (
                  <div style={{ padding: '6px 8px', color: '#6b7280' }}>
                    No stored scans yet today. Auto scans land about 09:48 ET, then 10:12, 10:32 and 11:12.
                  </div>
                )}
                {!isLoadingSavedScans && savedScans.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setIsSavedScanMenuOpen(false);
                      if (onLoadSavedScan) onLoadSavedScan(s);
                    }}
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      gap: '10px',
                      padding: '6px 8px',
                      border: 'none',
                      background: loadedScan?.id === s.id ? '#eef2ff' : 'transparent',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: '#1f2937'
                    }}
                  >
                    <span style={{ fontFamily: 'monospace' }}>{formatStoredScanTime(s.timestamp)}</span>
                    <span>{s.regime ? `${getSentimentEmoji(s.regime)} ${s.regime}` : '—'}</span>
                    <span style={{ color: '#6b7280' }}>{s.source === 'auto' ? 'auto' : 'manual'} · {s.pickCount ?? '?'} picks</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Stock Count Dropdown */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            {showCustomInput ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="number"
                  value={customCount}
                  onChange={(e) => setCustomCount(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomCountSubmit()}
                  placeholder="50-500"
                  style={{
                    width: '60px',
                    padding: '4px 8px',
                    fontSize: '0.85em',
                    borderRadius: '4px',
                    border: 'none',
                    textAlign: 'center'
                  }}
                  min="50"
                  max="500"
                  autoFocus
                />
                <button
                  onClick={handleCustomCountSubmit}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.8em',
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✓
                </button>
                <button
                  onClick={() => setShowCustomInput(false)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.8em',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsStockCountMenuOpen(!isStockCountMenuOpen)}
                disabled={isLoading}
                style={{
                  padding: '4px 10px',
                  fontSize: '0.85em',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span>{stockCount}</span>
                <span style={{ fontSize: '0.8em' }}>▼</span>
              </button>
            )}
            {isStockCountMenuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: '100px',
                overflow: 'hidden'
              }}>
                {stockCountOptions.map((count) => (
                  <div
                    key={count}
                    onClick={() => handleStockCountSelect(count)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: stockCount === count ? '#eff6ff' : 'transparent',
                      borderBottom: '1px solid #f3f4f6',
                      fontSize: '0.9em'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = stockCount === count ? '#eff6ff' : '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = stockCount === count ? '#eff6ff' : 'transparent'}
                  >
                    <span style={{ fontWeight: stockCount === count ? 'bold' : 'normal' }}>{count}</span>
                    {stockCount === count && <span style={{ color: '#22c55e' }}>✓</span>}
                  </div>
                ))}
                <div
                  onClick={() => handleStockCountSelect('custom')}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backgroundColor: 'transparent',
                    fontSize: '0.9em',
                    color: '#6b7280'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span>⚙️</span>
                  <span>Custom</span>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={onAnalyze}
            disabled={isLoading}
            style={{
              padding: '6px 12px',
              fontSize: '0.85em',
              backgroundColor: isLoading ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => !isLoading && (e.target.style.backgroundColor = 'rgba(255,255,255,0.3)')}
            onMouseOut={(e) => !isLoading && (e.target.style.backgroundColor = 'rgba(255,255,255,0.2)')}
          >
            {isLoading ? (isAnthropicLoading && isOpenaiLoading ? '⏳ Analyzing...' : '⏳ Waiting...') : '🔄 Refresh'}
          </button>
          {hasBothResults && riskAssessment && !riskAssessment.error && !isLoading && (
            <button
              onClick={onSaveScan}
              disabled={isScanSaved || isLoading}
              title={isScanSaved ? 'Scan already saved' : 'Save scan to database'}
              style={{
                padding: '6px 12px',
                fontSize: '0.85em',
                backgroundColor: isScanSaved ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isScanSaved ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => !isScanSaved && (e.target.style.backgroundColor = 'rgba(255,255,255,0.3)')}
              onMouseOut={(e) => !isScanSaved && (e.target.style.backgroundColor = 'rgba(255,255,255,0.2)')}
            >
              {isScanSaved ? '✓ Saved' : '💾 Save Scan'}
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              padding: '0',
              width: '32px',
              height: '32px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '20px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px'
      }}>
        {loadedScan && (
          <div style={{
            margin: '0 0 10px',
            padding: '8px 12px',
            borderRadius: '8px',
            background: '#eef2ff',
            color: '#3730a3',
            fontSize: '0.9em'
          }}>
            📂 Loaded {loadedScan.source === 'auto' ? 'auto' : 'manual'} scan {formatStoredScanTime(loadedScan.timestamp)} (scan {loadedScan.id}) · read-only, no AI cost · click 🔄 Refresh for a fresh scan
          </div>
        )}
        {!hasResults && !isLoading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '2em', marginBottom: '12px' }}>🤖</div>
            <div style={{ marginBottom: '16px' }}>
              Click the button above to analyze current page stocks with AI
            </div>
            <button
              onClick={onAnalyze}
              style={{
                padding: '10px 24px',
                fontSize: '1em',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              🚀 Start Analysis
            </button>
          </div>
        ) : (
          <>
            {/* Progressive Loading Status */}
            {isLoading && (
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '12px',
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                {/* Claude Status */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: isAnthropicLoading ? '#faf5ff' : (anthropicResult?.error ? '#fef2f2' : '#f0fdf4')
                }}>
                  <span style={{ fontSize: '1.1em' }}>🟣</span>
                  <span style={{ fontWeight: '500', color: '#7c3aed' }}>Claude</span>
                  {isAnthropicLoading ? (
                    <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>⏳ Loading...</span>
                  ) : anthropicResult?.error ? (
                    <span style={{ color: '#dc2626', fontSize: '0.9em' }}>❌ Error</span>
                  ) : (
                    <span style={{ color: '#22c55e', fontSize: '0.9em' }}>✅ Done</span>
                  )}
                </div>

                {/* OpenAI Status */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: isOpenaiLoading ? '#f0fdf4' : (openaiResult?.error ? '#fef2f2' : '#f0fdf4')
                }}>
                  <span style={{ fontSize: '1.1em' }}>🟢</span>
                  <span style={{ fontWeight: '500', color: '#059669' }}>GPT</span>
                  {isOpenaiLoading ? (
                    <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>⏳ Loading...</span>
                  ) : openaiResult?.error ? (
                    <span style={{ color: '#dc2626', fontSize: '0.9em' }}>❌ Error</span>
                  ) : (
                    <span style={{ color: '#22c55e', fontSize: '0.9em' }}>✅ Done</span>
                  )}
                </div>

                {/* Risk Assessment Status */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: isRiskLoading ? '#fef3c7' :
                    (riskAssessment?.error ? '#fef2f2' :
                     riskAssessment ? '#f0fdf4' : '#f9fafb')
                }}>
                  <span style={{ fontSize: '1.1em' }}>🛡️</span>
                  <span style={{ fontWeight: '500', color: '#92400e' }}>Risk</span>
                  {isRiskLoading ? (() => {
                    const done = riskAssessment?.stockRisks?.length || 0;
                    const total = (() => {
                      const syms = new Set();
                      anthropicResult?.topPicks?.forEach(p => syms.add(p.symbol));
                      openaiResult?.topPicks?.forEach(p => syms.add(p.symbol));
                      return syms.size;
                    })();
                    const label = total > 0
                      ? `⏳ Analyzing risk: ${done} of ${total} picks done`
                      : '⏳ Assessing...';
                    return <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>{label}</span>;
                  })() : riskAssessment?.error ? (
                    <span style={{ color: '#dc2626', fontSize: '0.9em' }}>❌ Error</span>
                  ) : riskAssessment ? (
                    <span style={{ color: '#22c55e', fontSize: '0.9em' }}>✅ Done</span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>Waiting</span>
                  )}
                </div>
              </div>
            )}

            {/* Waiting message - show only when nothing has returned yet */}
            {isLoading && !hasAnyResult && (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#6b7280'
              }}>
                <div style={{ fontSize: '2em', marginBottom: '12px' }}>🔍</div>
                <div style={{ fontSize: '1em', color: '#4b5563' }}>
                  Analyzing stocks with AI...
                </div>
                <div style={{ fontSize: '0.9em', color: '#9ca3af', marginTop: '8px' }}>
                  Results will display as each AI completes
                </div>
              </div>
            )}

            {/* Comparison Summary - shows progressively (one AI's picks visible when ready, other shows placeholder) */}
            {hasAnyResult && (
              <ComparisonSummary anthropicResult={anthropicResult} openaiResult={openaiResult} riskAssessment={riskAssessment} onSymbolClick={openRiskModal} />
            )}

            {/* Risk Assessment Section — placed BEFORE Detailed Comparison
                so the user's natural workflow (scan candidates → check
                action plans → execute) flows downward without scroll-back. */}
            {(hasBothResults || isRiskLoading) && (
              <RiskAssessmentSection
                riskAssessment={riskAssessment}
                isLoading={isRiskLoading}
                anthropicResult={anthropicResult}
                openaiResult={openaiResult}
                stockData={stockData}
                onSymbolClick={openRiskModal}
                marketSentiment={marketSentiment}
                ctxAtScan={ctxAtScan}
                trendScore={trendScore}
                spreadClass={spreadClass}
                iwmSpyspread={iwmSpyspread}
                expectedPickCount={(() => {
                  const syms = new Set();
                  anthropicResult?.topPicks?.forEach(p => syms.add(p.symbol));
                  openaiResult?.topPicks?.forEach(p => syms.add(p.symbol));
                  return syms.size;
                })()}
              />
            )}

            {/* Detailed Top Picks Comparison — the execution-parameter
                view (Entry / Stop / T1-T3). Consulted after the Risk
                Assessment table narrows the candidate list. */}
            {hasAnyResult && (
              <DetailedPicksComparison anthropicResult={anthropicResult} openaiResult={openaiResult} riskAssessment={riskAssessment} stockMap={modalStockMap} onSymbolClick={openRiskModal} />
            )}
          </>
        )}
      </div>

      {/* Single-stock Risk Assessment modal — opens from 7 trigger points. */}
      {selectedRiskSymbol && (
        <RiskModal
          symbol={selectedRiskSymbol}
          risk={selectedRisk}
          claudeAction={modalClaudeActions[selectedRiskSymbol]}
          gptAction={modalGptActions[selectedRiskSymbol]}
          stock={modalStockMap[selectedRiskSymbol]}
          pick={
            anthropicResult?.topPicks?.find((p) => p.symbol === selectedRiskSymbol) ||
            openaiResult?.topPicks?.find((p) => p.symbol === selectedRiskSymbol)
          }
          onClose={closeRiskModal}
          isLoading={isRiskLoading}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Single-stock Risk Assessment modal
// ────────────────────────────────────────────────────────────────────────

// Card-level background tint, matching the Top Picks chip / inline card colors
// so the modal feels like a focused expansion of the same surface.
const recCardBgFor = (rec) => {
  const r = (rec || '').toUpperCase();
  if (r.includes('SKIP'))   return '#fef2f2';
  if (r.includes('REDUCE')) return '#fefce8';
  if (r.includes('TAKE'))   return '#f0fdf4';
  return '#f9fafb';
};
const recAccentFor = (rec) => {
  const r = (rec || '').toUpperCase();
  if (r.includes('SKIP'))   return '#dc2626';
  if (r.includes('REDUCE')) return '#ca8a04';
  if (r.includes('TAKE'))   return '#16a34a';
  return '#6b7280';
};

// Small section header — same uppercase-gray-eyebrow used throughout
// the panel (Top Picks / Risk Assessment chip row), so the modal's
// internal hierarchy matches the rest of the surface.
const ModalSection = ({ title, children }) => (
  <div style={{ marginBottom: '14px' }}>
    <div style={{
      fontWeight: '700',
      fontSize: '0.78em',
      color: '#6b7280',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      marginBottom: '6px',
    }}>
      {title}
    </div>
    {children}
  </div>
);

// One metric pill: "Label  emoji Value" — same look as the Risk
// Assessment table cells, just laid out horizontally with a small
// gray label prefix. Emoji always comes BEFORE the number so the eye
// locks onto the tier color first.
const ModalMetric = ({ label, disp }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', whiteSpace: 'nowrap' }}>
    <span style={{ color: '#6b7280', fontSize: '0.85em' }}>{label}</span>
    <span style={{ color: disp.color, fontWeight: disp.fontWeight || '600', fontSize: '0.95em' }}>
      {disp.icon ? `${disp.icon} ` : ''}{disp.text}
    </span>
  </div>
);

// One Action Plan column — Entry / Stop / Target / T1/T2/T3 lines for
// one AI's parsed plan. Mirrors DetailedPickCard line 60-114 so the
// same content renders identically across surfaces. Extracted as a
// helper so the modal can stack two columns (Claude + GPT) side-by-side
// or fall back to a single column when only one AI returned a plan.
const renderActionPlanColumn = (parsed) => {
  if (!parsed) return null;
  const targetPct =
    parsed.takeProfitPrice && parsed.entryPrice
      ? ((parsed.takeProfitPrice - parsed.entryPrice) / parsed.entryPrice) * 100
      : null;
  return (
    <div style={{ fontSize: '0.95em', color: '#374151', lineHeight: '1.6' }}>
      {parsed.entry && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>🟢</span>
          <span><strong style={{ color: '#059669' }}>Entry:</strong> {parsed.entry}</span>
        </div>
      )}
      {parsed.stopLoss && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>🛑</span>
          <span><strong style={{ color: '#dc2626' }}>Stop:</strong> {parsed.stopLoss}</span>
        </div>
      )}
      {parsed.takeProfitPrice && parsed.entryPrice && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>🎯</span>
          <span>
            <strong style={{ color: '#7c3aed' }}>Target:</strong> ${parsed.takeProfitPrice.toFixed(2)}
            {targetPct != null && (
              <span style={{ color: '#6b7280', fontSize: '0.9em' }}>
                {' '}({targetPct >= 0 ? '+' : ''}{targetPct.toFixed(1)}%)
              </span>
            )}
          </span>
        </div>
      )}
      {parsed.targets && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
          <span style={{ flexShrink: 0 }}>📍</span>
          <div>
            <strong style={{ color: '#7c3aed' }}>T1/T2/T3:</strong>
            <div style={{ marginTop: '2px', marginLeft: '4px', fontSize: '0.95em', lineHeight: '1.6' }}>
              <div style={{ color: '#059669' }}>
                T1: ${parsed.targets.t1.price.toFixed(2)}{' '}
                <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t1.percent}%)</span>
              </div>
              <div style={{ color: '#2563eb' }}>
                T2: ${parsed.targets.t2.price.toFixed(2)}{' '}
                <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t2.percent}%)</span>
              </div>
              <div style={{ color: '#7c3aed' }}>
                T3: ${parsed.targets.t3.price.toFixed(2)}{' '}
                <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t3.percent}%)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RiskModal = ({ symbol, risk, claudeAction, gptAction, stock, pick, onClose, isLoading }) => {
  const recStyle = recStyleFor(risk?.recommendation);
  const cardBg = recCardBgFor(risk?.recommendation);
  const accent = recAccentFor(risk?.recommendation);

  // Action plans — parsed strings (parsed.entry = "buy above $112.05"
  // etc.) are what DetailedPickCard renders. Compute both Claude's and
  // GPT's so the modal can show them side-by-side when both are present;
  // fall back to a single column when only one AI returned a plan.
  const claudeParsed = claudeAction ? parseAction(claudeAction) : null;
  const gptParsed = gptAction ? parseAction(gptAction) : null;

  // Key Metrics still uses one preferred plan (Claude > GPT) — the
  // aggregated risk view per stock, matching renderRiskTable's
  // table-view convention.
  const action = claudeAction || gptAction;
  const m = action ? computeMetrics(action) : null;
  const vwapDist = m && stock?.vwap != null ? computeVwapDistance(m.stop, stock.vwap) : null;


  const scoreDisp = riskScoreDisplay(risk?.riskScore);
  const capTier = stock?.marketCap ? getCapTier(stock.marketCap) : null;
  const capLabel = capTier ? CAP_TIER_LABELS[capTier] : null;
  const sectorLabel = getSectorChip(stock?.sicCode, stock?.sicDescription);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: cardBg,
          borderRadius: '8px',
          padding: '22px 28px',
          maxWidth: '900px',
          width: '92%',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
          borderLeft: `4px solid ${accent}`,
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px',
          paddingBottom: '12px',
          borderBottom: `1px solid ${accent}33`,
        }}>
          <span style={{ fontSize: '1.4em', fontWeight: '700' }}>{symbol}</span>
          {risk && (
            <>
              {scoreDisp.icon && <span style={{ fontSize: '1.05em' }}>{scoreDisp.icon}</span>}
              <span style={{ color: scoreDisp.color, fontWeight: '600' }}>
                {scoreDisp.text}
              </span>
              <span style={{
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: recStyle.bg,
                color: recStyle.color,
                fontSize: '0.85em',
                fontWeight: '600',
              }}>
                {risk.recommendation}
              </span>
              {risk.bothAIsAgree && (
                <span style={{ color: '#16a34a', fontSize: '0.9em' }}>
                  ✓ Both AIs agree
                </span>
              )}
            </>
          )}
          {/* Personalization badges — Caution takes priority over Proven,
              matching the DetailedPickCard logic. Risk-deep-dive view
              benefits from seeing "matches my proven pattern" or "looks
              like my losing pattern" right next to the recommendation. */}
          {pick?.matchesUserSuccessPattern && !pick?.matchesUserAvoidPattern && (
            <span style={{
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '0.75em',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              color: '#92400e',
              border: '1px solid #f59e0b',
            }} title={`Matches your proven pattern: ${pick.userSuccessPatternName || 'successful pattern'}`}>
              ✨ Proven
            </span>
          )}
          {pick?.matchesUserAvoidPattern && (
            <span style={{
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '0.75em',
              fontWeight: '600',
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #dc2626',
            }} title={`Similar to losing pattern: ${pick.userAvoidPatternName || 'avoid pattern'}`}>
              ⚠️ Caution
            </span>
          )}
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto',
              border: 'none',
              background: 'transparent',
              fontSize: '1.4em',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0 6px',
            }}
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>

        {/* Cap + Sector subline — same emoji language as Context Strip Row 2 */}
        {(capLabel || sectorLabel) && (
          <div style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            color: '#4b5563',
            fontSize: '0.9em',
            marginBottom: '16px',
          }}>
            {capLabel && (
              <span title={`${capLabel.name} cap: ${capLabel.desc}`}>
                {capLabel.emoji} <strong>{capLabel.name}</strong>
              </span>
            )}
            {capLabel && sectorLabel && <span style={{ color: '#d1d5db' }}>·</span>}
            {sectorLabel && (
              <span>{sectorLabel.emoji} <strong>{sectorLabel.name}</strong></span>
            )}
          </div>
        )}

        {/* Body */}
        {!risk && isLoading && (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
            Loading risk for {symbol}…
          </div>
        )}
        {!risk && !isLoading && (
          <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
            No risk assessment available for {symbol}.
          </div>
        )}

        {risk && (
          <>
            {/* Key Metrics — same Pattern C tier emoji + colors as the
                Risk Assessment table row */}
            {m && (
              <ModalSection title="Key Metrics">
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <ModalMetric label="R:R"        disp={rrDisplay(m.rr)} />
                  <ModalMetric label="Stop-Entry" disp={lossPctDisplay(m.riskPct)} />
                  {vwapDist != null && (
                    <ModalMetric label="Stop-VWAP" disp={stopVwapDisplay(vwapDist)} />
                  )}
                  {stock?.relativeVolume != null && (
                    <ModalMetric label="RVOL" disp={rvolDisplay(stock.relativeVolume)} />
                  )}
                  {stock?.gapPercent != null && (
                    <ModalMetric label="Gap" disp={gapDisplay(stock.gapPercent)} />
                  )}
                </div>
              </ModalSection>
            )}

            {/* Action Plan — side-by-side Claude vs GPT when both AIs
                returned a plan, single column when only one did.
                Vertical layout + emoji prefixes mirror DetailedPickCard
                line 60-114 byte-for-byte so the content reads identically
                across surfaces. */}
            {(claudeParsed || gptParsed) && (
              <ModalSection title="Action Plan">
                {claudeParsed && gptParsed ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <div style={{
                        fontSize: '0.85em',
                        fontWeight: '600',
                        color: '#7c3aed',
                        marginBottom: '6px',
                        paddingBottom: '4px',
                        borderBottom: '1px solid #ede9fe',
                      }}>
                        Claude
                      </div>
                      {renderActionPlanColumn(claudeParsed)}
                    </div>
                    <div>
                      <div style={{
                        fontSize: '0.85em',
                        fontWeight: '600',
                        color: '#059669',
                        marginBottom: '6px',
                        paddingBottom: '4px',
                        borderBottom: '1px solid #d1fae5',
                      }}>
                        GPT
                      </div>
                      {renderActionPlanColumn(gptParsed)}
                    </div>
                  </div>
                ) : (
                  renderActionPlanColumn(claudeParsed || gptParsed)
                )}
              </ModalSection>
            )}

            {risk.rationale && (
              <ModalSection title="Why this rating">
                <div style={{
                  fontSize: '0.95em',
                  color: '#374151',
                  fontStyle: 'italic',
                  lineHeight: '1.5',
                }}>
                  {risk.rationale}
                </div>
              </ModalSection>
            )}

            {/* Pattern Match — Phase 2 personalization note. Single
                rule across all surfaces: ✨ Proven / ⚠️ Caution signal
                appears in (1) the header badge for at-a-glance scan
                and (2) the yellow/red highlight block's *color* — no
                inner badge. The section title already anchors the
                block, and the colored background carries the status. */}
            {pick?.personalizationNote && (() => {
              const isCaution = pick.matchesUserAvoidPattern;
              return (
                <ModalSection title="Pattern Match">
                  <div style={{
                    background: isCaution ? '#fef2f2' : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    borderLeft: isCaution ? '3px solid #dc2626' : '3px solid #f59e0b',
                    padding: '10px 12px',
                    borderRadius: '4px',
                    fontSize: '0.9em',
                    color: isCaution ? '#991b1b' : '#78350f',
                    fontWeight: '500',
                    lineHeight: '1.5',
                  }}>
                    {pick.personalizationNote.replace(/^[✨⚠️]\s*/, '')}
                  </div>
                </ModalSection>
              );
            })()}

            {risk.riskFactors?.length > 0 && (
              <ModalSection title="Risk Factors">
                <ul style={{
                  margin: 0,
                  paddingLeft: '20px',
                  fontSize: '0.9em',
                  color: '#374151',
                  lineHeight: '1.6',
                }}>
                  {risk.riskFactors.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </ModalSection>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AiAnalysisPanel;
